// @ts-nocheck
/**
 * Drag-and-drop schedule grid — one day at a time.
 *
 * Rows are time slots, columns are courts (grouped by venue). Cells only exist
 * where `rle_resolved_availability` says the court is open, so a court that
 * opens late simply has no cells before it opens.
 *
 * Every drop is a targeted update of just the affected games through the
 * `grid_drop` action, which sets manually_adjusted = true on each game it
 * moves. Blocking rules (team already playing, cell outside open hours, court
 * double-booked) refuse the drop; rest / cap / travel / approved-request
 * problems are applied and reported as warning markers instead.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, MeasuringStrategy,
  pointerWithin, useDraggable, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import { Card, CardContent } from '@/components/ui/card';
import { resolveDivisionColors, type ThemeMode } from '@/lib/divisionPalette';
import { useDivisionColors } from '@/hooks/useDivisionColors';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
// No tooltip primitives here on purpose: a portal per card is what crashed
// this page. The grid uses one plain fixed-position HoverDetail instead.

import { AlertTriangle, Loader2, Inbox, RefreshCw, CalendarClock, ArrowLeftRight, CalendarRange, Save, Undo2, Redo2, Circle, ChevronLeft, ChevronRight, ChevronDown, History as HistoryIcon } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { formatTime, formatCalendarDate } from '@/lib/dates';
import { CONSTRAINT_TYPE_LABELS } from '@/lib/constraints/humanize';
import { formatGameNumber, shortGameNumber } from '@/lib/gameNumber';
import { buildIssueMap, issueLevel } from '@/lib/scheduleIssues';
import { buildGameCardAria } from '@/lib/gameCardAria';
import { buildTeamGameIndex, companionMark, type CompanionMark } from '@/lib/dragCompanions';
import { teamsOverDailyCap, dailyCapLines } from '@/lib/dailyCap';
import { announceScheduleChange } from '@/lib/scheduleBus';
import ScheduleCheckPanel, { type CheckRow } from '@/components/admin/schedule/ScheduleCheckPanel';

const ROW_HEIGHT = 86;
const VIRTUALIZE_ABOVE_CELLS = 300;
const OVERSCAN = 4;

/** One shared visual vocabulary for both move legends and destination cells. */
const MOVE_CELL_STYLE = {
  blocked: 'bg-muted/70 opacity-45',
  busy: 'bg-info-light',
  discouraged: 'bg-warning-light',
  companion: 'bg-foreground/20',
} as const;

/** The "show every day on one canvas" pseudo-date. */
const ALL_DAYS = '__all__';



const toMin = (t?: string | null) => {
  if (!t) return null;
  const [h, m] = String(t).split(':');
  const hh = Number(h), mm = Number(m);
  return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
};
const fromMin = (n: number) =>
  `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

const overlaps = (aS: number, aE: number, bS: number, bE: number) => aS < bE && bS < aE;

/** The date a request applies to, in the shapes the placer already reads. */
const constraintDate = (c: any) => {
  const p = c?.parameters ?? {};
  const raw = p.blackout_date ?? p.target_date ?? p.date ?? null;
  return raw ? String(raw).slice(0, 10) : null;
};

/** Plain-English window for the hover panel. */
function constraintWindow(c: any) {
  const p = c?.parameters ?? {};
  const d = constraintDate(c);
  const day = d ? formatCalendarDate(d, 'EEE MMM d', d) : '';
  if (c.constraint_type === 'team_blackout') {
    return [day, `${formatTime(p.start_time, '?')}–${formatTime(p.end_time, '?')}`].filter(Boolean).join(' ');
  }
  if (c.constraint_type === 'must_play_before') {
    return [day, `finish by ${formatTime(p.target_time, '?')}`].filter(Boolean).join(' · ');
  }
  if (c.constraint_type === 'must_play_after') {
    return [day, `start after ${formatTime(p.target_time, '?')}`].filter(Boolean).join(' · ');
  }
  return day;
}

/**
 * Does placing a game on `date` from `start` to `end` (minutes) break this
 * approved request? Same three shapes, same comparisons, as `grid_drop`.
 */
function breaksConstraint(c: any, date: string, start: number, end: number) {
  const p = c?.parameters ?? {};
  const d = constraintDate(c);
  if (c.constraint_type === 'team_blackout') {
    const from = toMin(p.start_time), to = toMin(p.end_time);
    if (d !== date || from == null || to == null) return false;
    return overlaps(start, end, from, to);
  }
  if (c.constraint_type === 'must_play_before') {
    const limit = toMin(p.target_time);
    if (limit == null || (d && d !== date)) return false;
    return end > limit;
  }
  if (c.constraint_type === 'must_play_after') {
    const limit = toMin(p.target_time);
    if (limit == null || (d && d !== date)) return false;
    return start < limit;
  }
  return false;
}

/**
 * Division colours come from the one palette module. `schedule_color` on
 * event_division_format_overrides wins; otherwise the division string is
 * hashed into the palette, so 16u is the same colour on every page and reload.
 * Colour is never the only signal — the division name is always on the card.
 */
function buildDivisionColors(
  divisions: string[],
  stored: Record<string, string>,
  mode: ThemeMode,
) {
  const map: Record<string, { border: string; chipBg: string; chipText: string }> = {};
  for (const d of divisions) {
    const c = resolveDivisionColors(d, stored[d] ?? null, mode);
    map[d] = { border: c.stripe, chipBg: c.tint, chipText: c.text };
  }
  return map;
}


/**
 * The amber dot on a card with a back-to-back: a button that approves the
 * pair on the spot. Events stop here so the click never picks the game up.
 */
function B2BDot({ pairs, onApprove }: { pairs: any[]; onApprove: (item: any) => Promise<void> | void }) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="b2b-dot"
          onPointerDown={stop}
          onClick={stop}
          onKeyDown={stop}
          aria-label="Warning — back-to-back. Open to approve."
          title="Back-to-back — click to approve"
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
        >
          <Circle className="h-3 w-3 fill-amber-500 text-card" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-2 text-[12px]"
        onPointerDown={stop}
        onClick={stop}
        onKeyDown={stop}
      >
        <div className="mb-1 font-medium">Back-to-back</div>
        <ul className="space-y-2">
          {pairs.map((p: any) => {
            const key = `${p.registration_id}|${p.game_a_id}|${p.game_b_id}`;
            return (
              <li key={key} className="space-y-1">
                <div className="text-foreground">{p.message}</div>
                <Button
                  size="sm" variant="outline" className="h-7"
                  disabled={busyKey === key}
                  data-testid="b2b-approve-card"
                  onClick={async () => { setBusyKey(key); try { await onApprove(p); } finally { setBusyKey(null); } }}
                >
                  {busyKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Approve'}
                </Button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Card body — shared by the in-grid card and the drag overlay. */
function GameBody({
  g, warns = [], dim = false, inTray = false, lifted = false, ghost = false, color = null,
  issue = null, issueDetails = null, b2bPairs = null, onApproveB2B = null, moveMode = false,
}: any) {
  // `issue` is the publish check's verdict for this game: 'error' | 'warning'.
  // At any moment a card's fill means ONE thing: a card with a problem is
  // filled with the problem colour (red error, amber warning, error wins) and
  // its division is still named in its own text; every other card is filled
  // by division. In move mode the problem fills come off and cards return to
  // division, so the move colours on the cells stay the only signal.
  const hasWarning = issue === 'warning' || warns.length > 0 || (issueDetails?.warnings?.length ?? 0) > 0;
  const problem: 'error' | 'warning' | null = moveMode ? null : issue === 'error' ? 'error' : hasWarning ? 'warning' : null;
  const stateBorder = !problem
    ? 'border-0'
    : problem === 'error' ? 'border-2 border-error' : 'border-2 border-warning';
  const fill = problem === 'error'
    ? 'color-mix(in srgb, hsl(var(--error)) 42%, hsl(var(--card)))'
    : problem === 'warning'
      ? 'color-mix(in srgb, hsl(var(--warning)) 50%, hsl(var(--card)))'
      : color ? `color-mix(in srgb, ${color.border} 34%, hsl(var(--card)))` : undefined;
  return (
    <div
      data-card-state={issue === 'error' ? 'error' : hasWarning ? 'warning' : 'ok'}
      data-card-fill={problem ?? 'division'}
      style={fill ? { backgroundColor: fill } : undefined}

      className={[
        'h-full w-full overflow-hidden rounded px-2 py-1.5 text-[12px] leading-tight transition-all bg-card text-foreground',
        stateBorder,
        dim ? 'opacity-30' : '',
        inTray ? 'w-[240px] shrink-0' : '',
        ghost ? 'opacity-0' : '',
        lifted
          ? 'shadow-lg ring-2 ring-foreground rotate-1'
          : 'hover:-translate-y-0.5 hover:shadow-md',
      ].join(' ')}
    >
      <div className="flex items-center gap-1 text-[11px]">
        {g.division && (
          <span
            className="rounded bg-card/80 px-1 font-semibold"
            style={color ? { color: color.chipText } : undefined}
          >
            {g.division}
          </span>
        )}
        {g.game_number != null && (
          <span className="shrink-0 rounded bg-card/80 px-1 font-semibold text-foreground/80">
            {shortGameNumber(g)}
          </span>
        )}
        {/* A crossover keeps its division colour like every other game — the
            word alone, in red, says what it is, so colour never means two
            things at once. */}
        {g.game_type === 'crossover' ? (
          <span className="rounded bg-card px-1 font-bold text-error" title="Crossover — counts toward both teams' records">
            Crossover
          </span>
        ) : (g.pool_name || g.bracket_label) && (
          <span className="text-foreground/75 truncate">
            {g.pool_name ? poolLabel(g.pool_name) : g.bracket_label}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 shrink-0">
          {/* Same determination as the "With a scheduling request" filter. */}
          {g.has_request && (
            <CalendarClock className="h-3 w-3 text-primary" aria-label="Has an approved scheduling request" />
          )}
          {/* Colour is never the only signal: a filled circle is an error, a
              triangle a warning, so the state survives grey printing. */}
          {issue === 'error' && (
            <Circle className="h-3 w-3 fill-error text-card" aria-label="Error — this cannot be published" />
          )}
          {hasWarning && (b2bPairs?.length && onApproveB2B ? (
            <B2BDot pairs={b2bPairs} onApprove={onApproveB2B} />
          ) : (
            <Circle className="h-3 w-3 fill-warning text-card" aria-label="Warning" />
          ))}
        </span>
      </div>
      {/* Team names wrap onto a second line rather than being cut mid-word. */}
      <div className="flex min-w-0 items-start gap-1 font-medium text-foreground">
        <span className="break-words [overflow-wrap:anywhere] line-clamp-2">{g.team_a_name}</span>
        {g.team_a_level && (
          <span className="mt-px shrink-0 rounded bg-muted px-1 text-[9px] font-normal text-muted-foreground">
            {g.team_a_level}
          </span>
        )}
      </div>
      <div className="flex min-w-0 items-start gap-1 text-foreground/80">
        <span className="break-words [overflow-wrap:anywhere] line-clamp-2">vs {g.team_b_name}</span>
        {g.team_b_level && (
          <span className="mt-px shrink-0 rounded bg-muted px-1 text-[9px] text-muted-foreground">
            {g.team_b_level}
          </span>
        )}
      </div>

    </div>
  );
}

/** Pick-up target. Keyboard sensor works through the button role + listeners. */
function DraggableGame({ g, warns, dim, inTray, disabled, color, issue = null, issueDetails = null, courtLabel = null, b2bPairs = null, onApproveB2B = null, moveMode = false, holdLocked = false }: any) {
  // holdLocked: another game is held for click-to-move. This card is then a
  // swap TARGET, not something to pick up — a real mouse wobbles a few px
  // during a click, which used to start a drag of this card, put the held
  // game down and end on the same cell: no move, no message.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: g.id, disabled: disabled || holdLocked });
  // The name is identity plus a bare count; every sentence lives in the
  // description, so focus reads "… Court 1, 5:15pm. 1 warning." and the
  // detail that follows can be cut off.
  const aria = buildGameCardAria(g, courtLabel, issueDetails, warns);
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label={aria.label}
      aria-describedby={aria.descriptionId ?? undefined}
      title={aria.description.join(' ') || undefined}
      className={[
        'h-full touch-none rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled ? 'cursor-wait' : holdLocked ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing',
        inTray ? 'shrink-0' : 'w-full',
      ].join(' ')}
    >
      {/* Source cell reads as an empty outline while the card is in the air. */}
      <GameBody g={g} warns={warns} dim={dim} inTray={inTray} ghost={isDragging} color={color} issue={issue} issueDetails={issueDetails} b2bPairs={b2bPairs} onApproveB2B={onApproveB2B} moveMode={moveMode} />
      {/* Only present when there is something to describe — never empty. */}
      {aria.descriptionId && (
        <div id={aria.descriptionId} className="sr-only">
          {aria.description.map((sentence, i) => (
            <div key={i}>{sentence}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Pool names are usually already "Pool D" — never print "Pool Pool D". */
const poolLabel = (name?: string | null) =>
  !name ? null : /^pool\b/i.test(String(name).trim()) ? String(name).trim() : `Pool ${name}`;

const NO_WARNS: string[] = [];

/**
 * One card in the grid or the tray.
 *
 * Defined at module scope and memoised on purpose. It used to be declared
 * inside the grid's render body, which gave every card a brand-new component
 * type on every render — React threw away and rebuilt the whole DOM subtree
 * each time, which is what made dragging stutter. Hover no longer writes
 * coordinates either: the parent records only WHICH card is hovered, and the
 * single HoverDetail follows the pointer imperatively.
 */
const GameCard = memo(function GameCard({
  g, inTray = false, flagged = false, dim = false, warns = NO_WARNS,
  disabled = false, color = null, issue = null, issueDetails = null, courtLabel = null, onEnter, onLeave, onDown,
  b2bPairs = null, onApproveB2B = null,
  selected = false, held = false, moveMode = false, holdLocked = false, onSelect, onToggleMove,
  marked = false, markedCurrent = false, hidden = false,
}: any) {
  const handleEnter = useCallback((e: React.PointerEvent) => onEnter?.(g.id, e.clientX, e.clientY), [g.id, onEnter]);
  const handleLeave = useCallback(() => onLeave?.(g.id), [g.id, onLeave]);
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSelect) return;
    // Give the card real focus, so Space / Enter start the keyboard move.
    (e.currentTarget.querySelector('[aria-roledescription]') as HTMLElement | null)?.focus({ preventScroll: true });
    onSelect(g.id);
  }, [g.id, onSelect]);
  const showMark = marked && !moveMode;
  return (
    <div
      id={`grid-game-${g.id}`}
      data-card-id={g.id}
      data-marked={showMark ? (markedCurrent ? 'current' : 'yes') : undefined}
      style={showMark ? {
        outlineStyle: markedCurrent ? 'solid' : 'dashed',
        outlineWidth: markedCurrent ? '4px' : '3px',
        outlineColor: 'hsl(var(--foreground))',
        outlineOffset: markedCurrent ? '-4px' : '-3px',
        boxShadow: 'inset 0 0 0 2px hsl(var(--background))',
        zIndex: 6,
      } : undefined}
      className={[
        inTray ? 'relative shrink-0' : 'relative h-full',
        held ? 'rounded-md ring-[3px] ring-foreground ring-offset-2 ring-offset-background' : '',
        hidden && !held ? 'invisible' : '',
      ].join(' ')}
      data-filtered-out={hidden ? 'yes' : undefined}

      onPointerEnter={handleEnter}
      onPointerLeave={handleLeave}
      onPointerDown={onDown}
      onClick={handleClick}
    >
      <DraggableGame g={g} inTray={inTray} dim={dim} warns={warns} disabled={disabled} color={color} issue={issue} issueDetails={issueDetails} courtLabel={courtLabel} b2bPairs={b2bPairs} onApproveB2B={onApproveB2B} moveMode={moveMode} holdLocked={holdLocked} />
      {held && (
        <span className="pointer-events-none absolute left-1 top-1 z-10 rounded bg-primary px-1 text-[10px] font-semibold text-primary-foreground shadow">
          Moving
        </span>
      )}
      {(selected || held) && onToggleMove && (
        <button
          type="button"
          data-testid="move-this-game"
          aria-pressed={held}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onToggleMove(g.id); }}
          className="absolute bottom-1 right-1 z-10 rounded border border-primary bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Move this game
        </button>
      )}
    </div>
  );
});


/**
 * Hover detail — ONE instance for the entire grid.
 *
 * Deliberately NOT a Radix tooltip: a portal-capable overlay anchored to a card
 * that unmounts mid-drag is what produced the `removeChild` crash. This is a
 * plain fixed-position div driven by hover coordinates, so nothing is ever
 * attached to a node React is about to remove.
 */
function HoverDetail({ g, warns = [], issues = null, requests = [], courtName, venueName, posRef }: any) {
  const boxRef = useRef<HTMLDivElement | null>(null);

  // The panel follows the pointer by writing to the DOM node directly. Hover
  // coordinates never enter React state, so moving the mouse across the grid
  // does not re-render a single card.
  useEffect(() => {
    if (!g) return;
    const place = (x: number, y: number) => {
      const el = boxRef.current;
      if (!el) return;
      el.style.left = `${Math.min(x + 16, window.innerWidth - 320)}px`;
      el.style.top = `${Math.min(y + 16, window.innerHeight - 200)}px`;
    };
    place(posRef?.current?.x ?? 0, posRef?.current?.y ?? 0);
    const onMove = (e: PointerEvent) => place(e.clientX, e.clientY);
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [g, posRef]);

  if (!g) return null;
  const requestTypes: string[] = g.request_types ?? [];
  const scheduled = g.court_id && g.scheduled_start_time && g.scheduled_date;

  const style: any = { position: 'fixed', left: 0, top: 0, zIndex: 60, pointerEvents: 'none' };


  return (
    <div
      ref={boxRef}
      role="tooltip"
      style={style}
      className="max-w-[300px] space-y-1.5 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
    >
      {/* Who is this — first, always. */}
      <div className="space-y-0.5 text-sm font-semibold leading-snug">
        <div>
          {g.team_a_name}
          {g.team_a_level && (
            <span className="ml-1 text-[10px] font-normal text-muted-foreground">({g.team_a_level})</span>
          )}
        </div>
        <div>
          vs {g.team_b_name}
          {g.team_b_level && (
            <span className="ml-1 text-[10px] font-normal text-muted-foreground">({g.team_b_level})</span>
          )}
        </div>
      </div>

      <div className="text-muted-foreground">
        {[
          formatGameNumber(g) || null,
          g.division ? `Division ${g.division}` : null,
          g.game_type === 'crossover'
            ? "Crossover — counts toward both teams' records"
            : g.pool_name ? poolLabel(g.pool_name) : g.bracket_label || null,
        ].filter(Boolean).join(' · ')}
      </div>

      <div className="text-muted-foreground">
        {scheduled
          ? [
              [courtName, venueName].filter(Boolean).join(' — '),
              `${formatCalendarDate(g.scheduled_date, 'EEE MMM d', g.scheduled_date)} · ` +
                `${formatTime(g.scheduled_start_time)}–${formatTime(g.scheduled_end_time)}`,
            ].filter(Boolean).map((line) => <div key={line}>{line}</div>)
          : <div>Not scheduled</div>}
      </div>

      {/* The whole request: what it is, its window, hard or soft, paid or not. */}
      {requests.length > 0 ? (
        <div className="space-y-1 text-primary">
          {requests.map((r: any) => (
            <div key={r.id} className="flex items-start gap-1.5">
              <CalendarClock className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                <span className="font-medium">
                  {CONSTRAINT_TYPE_LABELS[r.constraint_type] ?? r.constraint_type}
                </span>
                {r.window ? <> — {r.window}</> : null}
                <br />
                <span className="text-[10px] text-muted-foreground">
                  {r.severity === 'hard' ? 'Hard — cannot be broken' : r.severity === 'soft' ? 'Soft — can be broken' : 'Preference'}
                  {' · '}
                  {r.fee_charged > 0 ? `Paid $${r.fee_charged}` : 'Free'}
                  {r.team_name ? ` · ${r.team_name}` : ''}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : g.has_request ? (
        <div className="flex items-start gap-1.5 text-primary">
          <CalendarClock className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            Approved scheduling request
            {requestTypes.length
              ? ` — ${requestTypes.map((t) => CONSTRAINT_TYPE_LABELS[t] ?? t).join(', ')}`
              : ''}
          </span>
        </div>
      ) : null}

      {/* Why the cell is coloured — the publish check's own words, one line
          per problem, errors first. */}
      {(issues?.errors?.length ?? 0) > 0 && (
        <div className="space-y-0.5 font-medium text-red-700">
          {issues.errors.map((e: string, i: number) => (
            <div key={i} className="flex items-start gap-1.5">
              <Circle className="mt-0.5 h-3 w-3 shrink-0 fill-red-600 text-red-700" />
              <span>{e}</span>
            </div>
          ))}
        </div>
      )}
      {(issues?.warnings?.length ?? 0) > 0 && (
        <div className="space-y-0.5 text-amber-700">
          {issues.warnings.map((w: string, i: number) => (
            <div key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {warns.length > 0 && (
        <div className="space-y-0.5 text-amber-700">
          {warns.map((w: string, i: number) => (
            <div key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




/**
 * A grid cell.
 *
 * Closed cells register as disabled so they never light up. While a drag is in
 * flight the cell also carries the legality worked out ONCE at drag start:
 *   blocked      — impossible or forbidden by a hard request; not a drop target
 *   busy         — one of the dragged game's teams is already playing here
 *   discouraged  — legal, but a judgement call (rest, cap, travel, soft request)
 */
function DropCell({ id, open, dragging, occupied, height, legality, issue = null, marked = false, rowMarked = false, holding = false, holdsHeld = false, onHeldClick, onHeldEnter, swapLabel = null, children }: any) {
  const state = dragging ? legality?.state ?? 'allowed' : 'allowed';
  const blocked = state === 'blocked';
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !open || !dragging || blocked });
  return (
    <div
      ref={setNodeRef}
      style={{ height }}
      title={dragging && legality?.reason ? legality.reason : dragging && occupied && swapLabel ? swapLabel : undefined}
      onPointerEnter={holding ? onHeldEnter : undefined}
      aria-disabled={blocked || undefined}
      data-testid={holding ? 'held-target' : undefined}
      onClickCapture={holding && !holdsHeld ? (e) => { e.stopPropagation(); e.preventDefault(); onHeldClick?.(); } : undefined}
      className={[
        'relative border-b border-l p-1 transition-colors',
        holding && !holdsHeld ? (open && !blocked ? 'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-primary' : 'cursor-not-allowed') : '',
        open ? 'bg-background' : 'bg-muted/40',
        dragging && !open
          ? 'bg-[repeating-linear-gradient(45deg,hsl(var(--muted))_0_6px,transparent_6px_12px)] opacity-60'
          : '',
        // These exact fills are also rendered by both move-mode keys.
        dragging && open && blocked ? `${MOVE_CELL_STYLE.blocked} cursor-not-allowed` : '',
        // The teams' own games elsewhere — informative, not a refusal.
        dragging && open && state === 'busy'
          ? MOVE_CELL_STYLE.busy : '',
        dragging && open && state === 'discouraged'
          ? MOVE_CELL_STYLE.discouraged : '',
        // Where these two teams already play. A dark band across the whole time
        // row, with the games themselves outlined — the row is what makes it
        // readable across eight courts at a glance, and dark collides with
        // none of the colours already in use.
        dragging && rowMarked ? 'bg-foreground/[0.10]' : '',
        dragging && marked ? MOVE_CELL_STYLE.companion : '',
        isOver && open && !blocked ? 'brightness-95' : '',
      ].join(' ')}
    >
      {children}
      {/* An occupied cell is a target too: a legal one swaps. The card covers
          the cell's own fill, so the key colour is laid over the card, and a
          Swap chip says what a click there will do. */}
      {dragging && occupied && open && !blocked && (
        <>
          {(state === 'busy' || state === 'discouraged') && (
            <span className={`pointer-events-none absolute inset-1 rounded-md opacity-70 mix-blend-multiply ${MOVE_CELL_STYLE[state]}`} />
          )}
          <span
            data-testid="swap-chip"
            className="pointer-events-none absolute right-2 top-1.5 z-10 inline-flex items-center gap-0.5 rounded bg-foreground px-1 text-[10px] font-semibold text-background shadow"
          >
            <ArrowLeftRight className="h-2.5 w-2.5" /> Swap
          </span>
        </>
      )}
    </div>
  );
}

/**
 * The tray doubles as the unschedule target. It gets its own amber-tinted
 * surface so an unplaced game never reads as a grid cell.
 */
function TrayDrop({ dragging, children }: any) {
  const { setNodeRef, isOver } = useDroppable({ id: 'tray', disabled: !dragging });
  return (
    <div
      ref={setNodeRef}
      className={[
        'rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/70 p-3 transition-colors',
        dragging ? 'border-amber-400' : '',
        isOver ? 'ring-2 ring-inset ring-amber-500 bg-amber-100' : '',
      ].join(' ')}
    >
      {children}
    </div>
  );
}



export default function ScheduleGrid({
  eventId,
  refreshToken = 0,
  focus = null,
  fillPane = false,
  onPlaceGames,
  placeLabel = 'Place games',
  placeDisabled = false,
  onOpenHistory,
  onSaveArrangement,
  placeControl,
  onFullscreenChange,
  onPublishAnyway,
  onUnplacedCount,
  unplacedReasons,
}: {
  eventId: string;
  refreshToken?: number;
  /** Jump to a day and flag one game — set by the pre-publish check's links. */
  focus?: { date: string | null; gameId: string; nonce: number } | null;
  /** The board owns the height of the pane: one scroller, and it is the board. */
  fillPane?: boolean;
  /** Opens the Place games dialog, which now carries the wave controls. */
  onPlaceGames?: () => void;
  placeLabel?: string;
  placeDisabled?: boolean;
  /** Opens the change history sheet over the board. */
  onOpenHistory?: () => void;
  /** Keeps this arrangement so the director can come back to it. */
  onSaveArrangement?: () => void;
  /** The page's own Place games control, rendered in this row. One only. */
  placeControl?: React.ReactNode;
  /** Tells the page to hide its header while the board fills the pane. */
  onFullscreenChange?: (v: boolean) => void;
  /** Opens the page's publish-with-problems confirmation, from the check panel. */
  onPublishAnyway?: () => void;
  /** Reports the tray's own unplaced count, so the publish confirmation and the
      "Unplaced games" badge are reading the same number and can never disagree. */
  onUnplacedCount?: (n: number) => void;
  /** game id -> why the last placement run could not find it a slot. */
  unplacedReasons?: Record<string, string>;
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  // First arrival opens on All days. A day picked in this session is kept
  // (per event) so coming back lands where the operator left it.
  const dayKey = `rle-grid-day:${eventId}`;
  const [date, setDate] = useState<string>(() => {
    try { return sessionStorage.getItem(`rle-grid-day:${eventId}`) || ALL_DAYS; } catch { return ALL_DAYS; }
  });
  useEffect(() => {
    try { if (date) sessionStorage.setItem(dayKey, date); } catch { /* private mode */ }
  }, [date, dayKey]);
  const [busy, setBusy] = useState(false);
  // The server-side move stack: what a click of Undo (or Redo) would do next.
  const [moveHead, setMoveHead] = useState<{ undo: any; redo: any }>({ undo: null, redo: null });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Fullscreen is a pure presentation flag: the tree never remounts, so the
  // scroll position, the filters and the loaded data all survive the toggle.
  const [fullscreen, setFullscreen] = useState(false);
  // Filled = the board owns the remaining height of the pane. Fill the window
  // is the same shape with the page header and control row hidden.
  const filled = fullscreen || fillPane;

  // gameId -> warning messages raised by the drops made in this session
  const [warnings, setWarnings] = useState<Record<string, string[]>>({});

  // The publish check report, refetched after anything that moves a game, so
  // the colour on the board is a property of the board and not of the last
  // time someone pressed Run check. It is the SAME report the pre-publish
  // panel prints — there is no second opinion computed here.
  const [check, setCheck] = useState<any>(null);
  const [badIndex, setBadIndex] = useState(0);
  // The check panel: the counter is the way in, and this is what it opens.
  const [checkOpen, setCheckOpen] = useState(false);
  const [checkFocusKind, setCheckFocusKind] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // GRID filters — matching games stay in place, non-matching ones are dimmed.
  const [fDivision, setFDivision] = useState('all');
  const [fPool, setFPool] = useState('all');
  const [fTeam, setFTeam] = useState('all');
  const [fType, setFType] = useState('all');
  const [fRequests, setFRequests] = useState(false);
  // Problem filters: one switch plus the three header counts (mutually exclusive).
  // "Highlight" row: toggles dim non-matching games (never remove); dropdowns narrow.
  const [fErrors, setFErrors] = useState(false);
  const [fWarnings, setFWarnings] = useState(false);
  const [fB2B, setFB2B] = useState(false);
  const [fCrossovers, setFCrossovers] = useState(false);

  // TRAY filters — independent of the grid, and these HIDE, because the tray
  // is a list: narrowing it to 16u must not narrow the grid you are placing into.
  const [tDivision, setTDivision] = useState('all');
  const [tPool, setTPool] = useState('all');
  const [tTeam, setTTeam] = useState('all');
  const [tType, setTType] = useState('all');
  const [tRequests, setTRequests] = useState(false);

  // One Filters popover for both sets; this only chooses which set it shows.
  const [filterScope, setFilterScope] = useState<'tray' | 'grid'>('grid');
  // The tray is a collapsed strip until someone opens it — or starts a drag.
  const [trayOpen, setTrayOpen] = useState(false);


  /**
   * The ONLY way the grid refetches: the Refresh button and the initial mount.
   * Drops never call this — `grid_drop` already reports exactly what changed.
   */
  const load = useCallback(async (preferredDate?: string) => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'grid_data', event_id: eventId },
      });
      if (res?.ok) {
        setData(res.data?.has_draft ? res.data : null);
        setDate((current) => {
          const requested = preferredDate ?? current;
          if (requested === ALL_DAYS) return ALL_DAYS;
          return requested && res.data?.dates?.includes(requested)
            ? requested
            : ALL_DAYS;
        });

      } else setData(null);
    } finally { setLoading(false); }
  }, [eventId]);


  useEffect(() => { void load(); }, [load, refreshToken]);

  /** Read-only. The one source of the colour on every cell. */
  const loadCheck = useCallback(async () => {
    if (!eventId) return;
    setChecking(true);
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'publish_check', event_id: eventId },
      });
      if (res?.ok) setCheck(res.data ?? null);
    } finally { setChecking(false); }
  }, [eventId]);

  useEffect(() => { void loadCheck(); }, [loadCheck, refreshToken]);

  // A link from the pre-publish check: switch day, ring the game, then let go.
  const [flaggedId, setFlaggedId] = useState<string | null>(null);
  useEffect(() => {
    if (loading || !focus?.gameId) return;
    if (focus.date) setDate(focus.date);
    setFlaggedId(focus.gameId);
    const el = document.getElementById(`grid-game-${focus.gameId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = window.setTimeout(() => setFlaggedId(null), 6000);
    return () => window.clearTimeout(t);
  }, [loading, focus?.nonce, focus?.gameId, focus?.date]);

  /** A brief ring on the cell a change just touched. */
  const flashGame = useCallback((id?: string | null) => {
    if (!id) return;
    setFlaggedId(id);
    window.setTimeout(() => setFlaggedId((cur) => (cur === id ? null : cur)), 4000);
  }, []);

  const sensors = useSensors(
    // A 5px activation distance: a click still selects, a small move drags.
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // Touch: a quarter-second press starts a drag, so a swipe still scrolls.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Board scrolling while a game is held. dnd-kit's own autoScroll looks for
  // the scrollable ancestors of the ACTIVE node — ours unmounts on drag start
  // (and may be in the tray), so it never finds the grid. We scroll the grid
  // scroller ourselves: edge auto-scroll (accelerating) and the wheel.
  // After each scroll we replay the pointer position so collision detection
  // re-runs and the drop target is the cell now under the pointer.
  const dragPtr = useRef<{ x: number; y: number; touch: boolean } | null>(null);
  const dragging = activeId != null;
  useEffect(() => {
    if (!dragging) return;
    const replay = () => {
      const p = dragPtr.current; if (!p) return;
      if (p.touch) return; // touch sensors re-read on the next finger move
      document.dispatchEvent(new MouseEvent('mousemove', { clientX: p.x, clientY: p.y, bubbles: true }));
    };
    const onMouse = (e: MouseEvent) => { if (e.isTrusted) dragPtr.current = { x: e.clientX, y: e.clientY, touch: false }; };
    const onTouch = (e: TouchEvent) => { const t = e.touches[0]; if (t) dragPtr.current = { x: t.clientX, y: t.clientY, touch: true }; };
    const onWheel = (e: WheelEvent) => {
      const sc = scrollRef.current; if (!sc) return;
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
      let dx = e.deltaX * unit, dy = e.deltaY * unit;
      if (e.shiftKey && !dx) { dx = dy; dy = 0; }
      sc.scrollLeft += dx; sc.scrollTop += dy;
      replay();
    };
    const EDGE = 70, MAX = 28;
    let raf = 0;
    const tick = () => {
      const sc = scrollRef.current, p = dragPtr.current;
      if (sc && p) {
        const r = sc.getBoundingClientRect();
        const inside = p.x >= r.left - 40 && p.x <= r.right + 40 && p.y >= r.top - 40 && p.y <= r.bottom + 40;
        const sp = (d: number) => (d < EDGE ? Math.ceil(MAX * Math.pow(1 - Math.max(d, 0) / EDGE, 2)) : 0);
        if (inside) {
          const vx = sp(r.right - p.x) - sp(p.x - r.left);
          const vy = sp(r.bottom - p.y) - sp(p.y - r.top);
          if (vx || vy) {
            const bl = sc.scrollLeft, bt = sc.scrollTop;
            sc.scrollLeft += vx; sc.scrollTop += vy;
            if (sc.scrollLeft !== bl || sc.scrollTop !== bt) replay();
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener('mousemove', onMouse, true);
    window.addEventListener('touchmove', onTouch, { capture: true, passive: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouse, true);
      window.removeEventListener('touchmove', onTouch, true);
      window.removeEventListener('wheel', onWheel);
      dragPtr.current = null;
    };
  }, [dragging]);



  const games: any[] = data?.games ?? [];
  const courts: any[] = data?.courts ?? [];
  const slotMinutes: number = data?.slot_minutes ?? 60;
  const gameMinutes: number = data?.game_duration_minutes ?? slotMinutes;

  // ─── Filter option lists ───────────────────────────────────────────────
  const divisions = useMemo(
    () => [...new Set(games.map((g) => g.division).filter(Boolean))].sort(), [games]);
  // One colour per division of THIS event, derived at render time.
  const { stored: divisionColorOverrides, mode: themeMode } = useDivisionColors(eventId);
  const divisionColors = useMemo(
    () => buildDivisionColors(divisions as string[], divisionColorOverrides, themeMode),
    [divisions, divisionColorOverrides, themeMode],
  );
  /** Pool names offered, narrowed to the chosen division when there is one. */
  const poolsFor = useCallback((division: string) => [...new Set(
    games.filter((g) => division === 'all' || g.division === division)
      .map((g) => g.pool_name).filter(Boolean),
  )].sort() as string[], [games]);

  /** Every team name we know, from the games on this board. */
  const teamNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of games) {
      if (g.team_a_registration_id) m.set(g.team_a_registration_id, g.team_a_name);
      if (g.team_b_registration_id) m.set(g.team_b_registration_id, g.team_b_name);
    }
    return m;
  }, [games]);

  /** "<division>||<pool>" → registration ids, straight from pool assignments. */
  const poolMembers: Record<string, string[]> = data?.pool_members ?? {};

  /**
   * The teams the dropdown offers.
   *  - division + pool → exactly that pool's teams (a pool is identified by
   *    division AND name, because two divisions can both have a "Pool A").
   *  - division only    → every team playing in that division.
   *  - neither          → every team on the board.
   * This narrows the dropdown only. The board's own filtering is untouched, so
   * a Pool A team's crossover against a Pool B side stays on the grid.
   */
  const teamsFor = useCallback((division: string, pool: string): [string, string][] => {
    let ids: string[];
    if (division !== 'all' && pool !== 'all') {
      ids = poolMembers[`${division}||${pool}`] ?? [];
    } else {
      const s = new Set<string>();
      for (const g of games) {
        if (division !== 'all' && g.division !== division) continue;
        if (g.team_a_registration_id) s.add(g.team_a_registration_id);
        if (g.team_b_registration_id) s.add(g.team_b_registration_id);
      }
      ids = [...s];
    }
    return [...new Set(ids)]
      .map((id) => [id, teamNames.get(id) ?? 'Team'] as [string, string])
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [games, teamNames, poolMembers]);

  /** One matcher, applied to whichever filter set the caller passes in. */
  const makeMatcher = (division: string, pool: string, type: string, team: string, requests: boolean) =>
    (g: any) => {
      if (division !== 'all' && g.division !== division) return false;
      if (pool !== 'all' && g.pool_name !== pool) return false;
      if (type !== 'all' && g.game_type !== type) return false;
      if (requests && !g.has_request) return false;
      if (team !== 'all'
        && g.team_a_registration_id !== team
        && g.team_b_registration_id !== team) return false;
      return true;
    };

  const matches = useCallback(
    makeMatcher(fDivision, fPool, fType, fTeam, fRequests),
    [fDivision, fPool, fType, fTeam, fRequests]);

  const trayMatches = useCallback(
    makeMatcher(tDivision, tPool, tType, tTeam, tRequests),
    [tDivision, tPool, tType, tTeam, tRequests]);

  const baseFiltersActive = fDivision !== 'all' || fPool !== 'all' || fType !== 'all' || fTeam !== 'all' || fRequests;
  const showOnlyActive = fRequests || fErrors || fWarnings || fB2B || fCrossovers;
  const problemFilterActive = fErrors || fWarnings || fB2B;
  const filtersActive = baseFiltersActive || problemFilterActive;
  const trayFiltersActive = tDivision !== 'all' || tPool !== 'all' || tType !== 'all' || tTeam !== 'all' || tRequests;
  // One badge for both sets, so nothing is ever silently filtered.
  const activeFilterCount =
    [fDivision, fPool, fType, fTeam].filter((v) => v !== 'all').length + (fRequests ? 1 : 0) + (fErrors ? 1 : 0) + (fWarnings ? 1 : 0) + (fB2B ? 1 : 0) +
    [tDivision, tPool, tType, tTeam].filter((v) => v !== 'all').length + (tRequests ? 1 : 0);

  // ─── The filter row edits one scope at a time ──────────────────────────
  const isGridScope = filterScope === 'grid';
  const scopeDivision = isGridScope ? fDivision : tDivision;
  const scopePool = isGridScope ? fPool : tPool;
  const scopeTeam = isGridScope ? fTeam : tTeam;
  const setScopeTeam = isGridScope ? setFTeam : setTTeam;
  const scopePools = poolsFor(scopeDivision);
  const scopeTeams = teamsFor(scopeDivision, scopePool);
  /** The heading the Teams box shows, so a short list never reads as a bug. */
  const allTeamsLabel =
    scopeDivision !== 'all' && scopePool !== 'all' ? `All teams in Pool ${scopePool} (${scopeTeams.length})`
      : scopeDivision !== 'all' ? `All teams in ${scopeDivision} (${scopeTeams.length})`
        : `All teams (${scopeTeams.length})`;

  /**
   * Picking a division or a pool wins: a team the new selection does not
   * contain is cleared rather than silently kept, and the pool stays.
   */
  const keepTeamOrClear = (division: string, pool: string) => {
    if (scopeTeam === 'all') return;
    const still = teamsFor(division, pool).some(([id]) => id === scopeTeam);
    if (!still) setScopeTeam('all');
  };
  const chooseDivision = (v: string) => {
    keepTeamOrClear(v, poolsFor(v).includes(scopePool) ? scopePool : 'all');
    if (!poolsFor(v).includes(scopePool)) (isGridScope ? setFPool : setTPool)('all');
    (isGridScope ? setFDivision : setTDivision)(v);
  };
  const choosePool = (v: string) => {
    keepTeamOrClear(scopeDivision, v);
    (isGridScope ? setFPool : setTPool)(v);
  };


  // ─── Rows / cells for the current view (one day, or all of them) ───────
  const viewDates: string[] = useMemo(() => {
    const all: string[] = data?.dates ?? [];
    if (date === ALL_DAYS) return all;
    return date ? [date] : [];
  }, [data, date]);
  const viewDateSet = useMemo(() => new Set(viewDates), [viewDates]);
  const allDays = date === ALL_DAYS;

  const dayGames = useMemo(
    () => games.filter((g) => g.court_id && g.scheduled_start_time && viewDateSet.has(g.scheduled_date)),
    [games, viewDateSet]);
  const allTrayGames = useMemo(
    () => games.filter((g) => !g.court_id || !g.scheduled_start_time || !g.scheduled_date),
    [games]);
  // The tray HIDES what its own filters exclude; the grid is untouched by them.
  const trayGames = useMemo(
    () => (trayFiltersActive ? allTrayGames.filter(trayMatches) : allTrayGames),
    [allTrayGames, trayFiltersActive, trayMatches]);

  // The publish confirmation says "N games are not placed" from THIS count —
  // the same array the tray badge shows — never a second total computed elsewhere.
  useEffect(() => {
    onUnplacedCount?.(allTrayGames.length);
  }, [onUnplacedCount, allTrayGames.length]);

  /** `${court_id}|${date}` -> open windows */
  const windowsByCourtDate = useMemo(() => {
    const m = new Map<string, Array<{ start: number; end: number }>>();
    for (const w of data?.windows ?? []) {
      if (!viewDateSet.has(w.session_date)) continue;
      const s = toMin(w.start_time), e = toMin(w.end_time);
      if (s == null || e == null) continue;
      const k = `${w.court_id}|${w.session_date}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push({ start: s, end: e });
    }
    return m;
  }, [data, viewDateSet]);

  /** date -> slot start times */
  const rowsByDate = useMemo(() => {
    const sets = new Map<string, Set<number>>();
    for (const d of viewDates) sets.set(d, new Set<number>());
    for (const [key, list] of windowsByCourtDate.entries()) {
      const d = key.slice(key.indexOf('|') + 1);
      const set = sets.get(d);
      if (!set) continue;
      for (const w of list) {
        for (let s = w.start; s + gameMinutes <= w.end; s += slotMinutes) set.add(s);
      }
    }
    // A hand-placed game that sits off the increment still needs a row.
    for (const g of dayGames) {
      const s = toMin(g.scheduled_start_time);
      const set = sets.get(g.scheduled_date);
      if (s != null && set) set.add(s);
    }
    const m = new Map<string, number[]>();
    for (const d of viewDates) m.set(d, [...(sets.get(d) ?? [])].sort((a, b) => a - b));
    return m;
  }, [windowsByCourtDate, dayGames, viewDates, slotMinutes, gameMinutes]);

  /**
   * One flat row list for the canvas. In All Days each day's banner is itself
   * a row of the same height, so virtualisation stays uniform arithmetic.
   */
  const flatRows = useMemo(() => {
    const out: Array<{ kind: 'day' | 'slot'; date: string; start: number }> = [];
    for (const d of viewDates) {
      const slots = rowsByDate.get(d) ?? [];
      if (slots.length === 0) continue;
      if (allDays) out.push({ kind: 'day', date: d, start: -1 });
      for (const s of slots) out.push({ kind: 'slot', date: d, start: s });
    }
    return out;
  }, [viewDates, rowsByDate, allDays]);

  const slotRowCount = useMemo(
    () => flatRows.reduce((n, r) => n + (r.kind === 'slot' ? 1 : 0), 0), [flatRows]);

  const cellOpen = useCallback((courtId: string, d: string, start: number) =>
    (windowsByCourtDate.get(`${courtId}|${d}`) ?? []).some(
      (w) => start >= w.start && start + gameMinutes <= w.end),
    [windowsByCourtDate, gameMinutes]);

  const gameAt = useMemo(() => {
    const m = new Map<string, any>();
    for (const g of dayGames) {
      const s = toMin(g.scheduled_start_time);
      if (s == null) continue;
      m.set(`${g.court_id}|${g.scheduled_date}|${s}`, g);
    }
    return m;
  }, [dayGames]);


  // ─── Approved requests, indexed by registration ────────────────────────
  // The server resolves which registrations each request belongs to; the grid
  // never re-derives that. Same rows the placer reads.
  const constraints: any[] = data?.constraints ?? [];
  const constraintsByReg = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const c of constraints) {
      for (const r of c.registration_ids ?? []) {
        if (!m.has(r)) m.set(r, []);
        m.get(r)!.push(c);
      }
    }
    return m;
  }, [constraints]);

  const teamsOfGame = useCallback(
    (g: any) => [g?.team_a_registration_id, g?.team_b_registration_id].filter(Boolean) as string[], []);

  const requestsForGame = useCallback((g: any) => {
    if (!g) return [];
    const out: any[] = [];
    const seen = new Set<string>();
    for (const t of teamsOfGame(g)) {
      const name = t === g.team_a_registration_id ? g.team_a_name : g.team_b_name;
      for (const c of constraintsByReg.get(t) ?? []) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        out.push({ ...c, team_name: name, window: constraintWindow(c) });
      }
    }
    return out;
  }, [constraintsByReg, teamsOfGame]);

  const venueOfCourt = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const c of courts) m.set(c.id, c.venue_id ?? null);
    return m;
  }, [courts]);

  /**
   * Per-slot legality for the game being dragged — computed ONCE, on drag
   * start, and held in state until the drag ends. Nothing here runs on hover.
   *
   * In All Days this covers every date on the canvas in the same single pass:
   * rest, cap and court conflicts are all evaluated against the games on the
   * CANDIDATE cell's own date, not the date the game started on.
   */
  const [legality, setLegality] = useState<Record<string, { state: string; reason: string }> | null>(null);
  /**
   * While a game is in the air: where those same two teams already play.
   * Worked out from the board the grid already has — no second query — and
   * indexed once per load, never per drag.
   */
  const teamGameIndex = useMemo(() => buildTeamGameIndex(games), [games]);
  const [dragMark, setDragMark] = useState<CompanionMark | null>(null);

  // Click-to-move. selectedId shows the "Move this game" button; heldId is a
  // game picked up through it. Both sit beside activeId (the drag) — a drag
  // start clears them.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [heldId, setHeldId] = useState<string | null>(null);
  /** The cell under the pointer while a game is held — drives the bar's preview line. */
  const [heldOver, setHeldOver] = useState<string | null>(null);
  const lastDragEndAt = useRef(0);

  /**
   * Per-division court allowlist, straight from grid_data. A division that is
   * absent may use every active court — empty means all, never none — so this
   * greys nothing unless the director restricted the division themselves.
   */
  const divisionCourts: Record<string, string[]> = (data as any)?.division_courts ?? {};
  const poolCourts: Record<string, string[]> = (data as any)?.pool_courts ?? {};
  const courtsAllowedFor = useCallback((division?: string | null, poolId?: string | null) => {
    // A pool with its own court list (Select Courts to Schedule) is narrower.
    const own = poolId ? poolCourts[String(poolId)] : null;
    if (own && own.length) return new Set(own);
    const list = division ? divisionCourts[String(division)] : null;
    if (!list || list.length === 0) return null;   // null = every court
    return new Set(list);
  }, [data]);

  const computeLegality = useCallback((gameId: string) => {
    const dragged = games.find((g: any) => g.id === gameId);
    if (!dragged) return {};
    const myTeams = teamsOfGame(dragged);
    const minRest = Math.max(0, Number(data?.rules?.min_rest_minutes ?? 0));
    const maxPerDay = Math.max(0, Number(data?.rules?.max_games_per_day ?? 0));
    const travelBuffer = Math.max(0, Number(data?.rules?.venue_travel_buffer_minutes ?? 15));
    const myRequests = requestsForGame(dragged);
    const draggedAllowed = courtsAllowedFor(dragged.division, dragged.pool_id);

    // Everything else already placed, grouped by the day it sits on.
    const placedByDate = new Map<string, any[]>();
    for (const g of dayGames) {
      if (g.id === gameId) continue;
      const start = toMin(g.scheduled_start_time);
      if (start == null || !Number.isFinite(start)) continue;
      const row = {
        id: g.id,
        court_id: g.court_id,
        start,
        end: toMin(g.scheduled_end_time) ?? start + gameMinutes,
        teams: teamsOfGame(g),
      };
      const list = placedByDate.get(g.scheduled_date) ?? [];
      list.push(row);
      placedByDate.set(g.scheduled_date, list);
    }

    // Games involving either of my teams — used for overlap, rest and travel,
    // where "one of these teams" is genuinely the right unit.
    const sameTeamByDate = new Map<string, any[]>();
    for (const [d, list] of placedByDate.entries()) {
      sameTeamByDate.set(d, list.filter((g: any) => g.teams.some((t: string) => myTeams.includes(t))));
    }

    const out: Record<string, { state: string; reason: string }> = {};
    for (const row of flatRows) {
      if (row.kind !== 'slot') continue;
      const d = row.date;
      const start = row.start;
      const end = start + gameMinutes;
      const placed = placedByDate.get(d) ?? [];
      const sameTeamToday = sameTeamByDate.get(d) ?? [];
      // The cap is per team. Counting the union of both teams' games printed a
      // total neither of them plays, which is how "five games" appeared for a
      // pair that plays three each.
      const capRows = teamsOverDailyCap({
        board: placedByDate.get(d) ?? [],
        teams: myTeams,
        maxPerDay,
        adding: 1,
      });

      for (const c of courts) {
        const key = `${c.id}|${d}|${start}`;
        const occupant = gameAt.get(key);
        if (occupant?.id === gameId) { out[key] = { state: 'allowed', reason: '' }; continue; }
        if (!cellOpen(c.id, d, start)) {
          out[key] = { state: 'blocked', reason: `${c.name} is not open at that time.` };
          continue;
        }
        // The division's own court list — the director's configuration, so
        // there is no soft version of it: the cell is simply not available.
        if (draggedAllowed && !draggedAllowed.has(c.id)) {
          out[key] = {
            state: 'blocked',
            reason: `${dragged.division} is not allowed on ${c.name}.`,
          };
          continue;
        }

        let state = 'allowed';
        let reason = '';
        const mark = (s: string, r: string) => {
          const rank: any = { allowed: 0, busy: 1, discouraged: 2, blocked: 3 };
          if (rank[s] > rank[state]) { state = s; reason = r; }
        };

        // ── Blocked: physically impossible ──
        for (const p of placed) {
          if (p.id === occupant?.id) continue; // dropping here is a swap
          if (!overlaps(start, end, p.start, p.end)) continue;
          if (p.court_id === c.id) mark('blocked', 'That court is already booked at this time.');
          if (p.teams.some((t: string) => myTeams.includes(t))) {
            mark('blocked', 'One of these teams is already playing then.');
          }
        }

        // ── Blocked: a hard approved request ──
        for (const r of myRequests) {
          if (r.severity !== 'hard') continue;
          if (breaksConstraint(r, d, start, end)) {
            mark('blocked', `${CONSTRAINT_TYPE_LABELS[r.constraint_type] ?? r.constraint_type} — ${r.team_name}: ${r.window}`);
          }
        }

        if (state !== 'blocked') {
          // ── Where these teams already are ──
          for (const p of sameTeamToday) {
            if (overlaps(start, end, p.start, p.end)) {
              mark('busy', 'One of these teams is already playing in this slot.');
            }
          }

          // ── Discouraged: soft and preference requests ──
          for (const r of myRequests) {
            if (r.severity === 'hard') continue;
            if (breaksConstraint(r, d, start, end)) {
              mark('discouraged', `Breaks a ${r.severity} request — ${r.team_name}: ${r.window}`);
            }
          }

          // ── Discouraged: rest, back-to-back, travel ──
          for (const p of sameTeamToday) {
            const gap = start >= p.end ? start - p.end : p.start - end;
            if (gap < 0) continue;
            if (minRest > 0 && gap < minRest) {
              mark('discouraged', `Only ${gap} min rest for one of these teams (minimum is ${minRest}).`);
            } else if (gap === 0) {
              mark('discouraged', 'Back-to-back with this team\'s other game.');
            }
            const here = venueOfCourt.get(c.id) ?? null;
            const there = venueOfCourt.get(p.court_id) ?? null;
            if (here && there && here !== there && gap < travelBuffer) {
              mark('discouraged', `Only ${gap} min to get between venues (buffer is ${travelBuffer}).`);
            }
          }

          // ── Discouraged: daily cap ──
          // Named, per team, one sentence each: two teams over the cap is two
          // lines, not one line with their totals added together.
          if (capRows.length) {
            mark(
              'discouraged',
              dailyCapLines(capRows, (id) => teamNames.get(id) ?? 'That team').join(' '),
            );
          }
        }

        out[key] = { state, reason };
      }
    }
    return out;
  }, [games, dayGames, flatRows, courts, gameAt, cellOpen, gameMinutes, data, requestsForGame, teamsOfGame, teamNames, venueOfCourt, courtsAllowedFor]);




  // ─── Virtualisation (kicks in once the canvas gets big — All Days will) ──
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(800);
  const totalCells = slotRowCount * Math.max(1, courts.length);
  const virtualize = totalCells > VIRTUALIZE_ABOVE_CELLS;
  const firstRow = virtualize ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN) : 0;
  const lastRow = virtualize
    ? Math.min(flatRows.length, Math.ceil((scrollTop + viewH) / ROW_HEIGHT) + OVERSCAN)
    : flatRows.length;
  const visibleRows = flatRows.slice(firstRow, lastRow);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setViewH(el.clientHeight || 800);
  }, [date, virtualize, fullscreen, fillPane]);

  // Fullscreen must not leave the page behind scrollable underneath it.
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [fullscreen]);

  // The page hides its header and control row while the board fills the pane.
  useEffect(() => { onFullscreenChange?.(fullscreen); }, [fullscreen, onFullscreenChange]);


  // ─── Hover detail (one shared instance) ────────────────────────────────
  // Only the hovered card's id lives in state. Coordinates go in a ref, so
  // moving the pointer across the grid never triggers a render.
  const [hoverId, setHoverId] = useState<string | null>(null);
  const hoverPos = useRef({ x: 0, y: 0 });
  const suppressHover = useRef(false);
  const clearHover = useCallback(() => setHoverId(null), []);
  const onCardEnter = useCallback((id: string, x: number, y: number) => {
    if (suppressHover.current) return;
    hoverPos.current = { x, y };
    setHoverId(id);
  }, []);
  const onCardLeave = useCallback((id: string) => {
    setHoverId((prev) => (prev === id ? null : prev));
  }, []);
  // Any change to the game list identity, the day, or a busy write invalidates
  // the hovered card: it may be about to unmount.
  useEffect(() => { setHoverId(null); }, [games, date, busy]);
  useEffect(() => { suppressHover.current = !!activeId || busy || !!heldId; }, [activeId, busy, heldId]);

  const putDown = useCallback(() => {
    setHeldId(null);
    setHeldOver(null);
    setLegality(null);
    setDragMark(null);
  }, []);

  /** A plain click only shows the Move button — never moves anything. */
  const selectCard = useCallback((id: string) => {
    if (Date.now() - lastDragEndAt.current < 300) return; // the click a drag release leaves behind
    setSelectedId(id);
  }, []);

  /** "Move this game": pick up, or put down if this game is already held. */
  const toggleMove = useCallback((id: string) => {
    if (heldId === id) { putDown(); return; }
    setHoverId(null);
    setLegality(computeLegality(id));
    setDragMark(companionMark(
      teamGameIndex,
      games.find((x: any) => x.id === id),
      (d) => formatCalendarDate(d, 'EEE d MMM', d),
    ));
    setHeldId(id);
    setSelectedId(id);
  }, [heldId, putDown, computeLegality, teamGameIndex, games]);

  // Escape puts a held game down; with nothing held it just hides the button.
  useEffect(() => {
    if (!heldId && !selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (heldId) putDown(); else setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [heldId, selectedId, putDown]);

  // Clicking anywhere off the selected card puts the button away (not while held:
  // then clicks are aimed at cells).
  useEffect(() => {
    if (!selectedId || heldId) return;
    const onDown = (e: PointerEvent) => {
      const el = (e.target as HTMLElement)?.closest?.('[data-card-id]');
      if (el?.getAttribute('data-card-id') !== selectedId) setSelectedId(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [selectedId, heldId]);

  // Changing day drops the hold — its cells may no longer be on screen.
  useEffect(() => { setHeldId(null); setSelectedId(null); setLegality(null); setDragMark(null); }, [date]);

  const heldGame = heldId ? games.find((g: any) => g.id === heldId) ?? null : null;
  /** What a click on the hovered cell will do — the same words the drag uses. */
  const heldPreview = useMemo(() => {
    if (!heldId || !heldOver) return null;
    const [courtId, d, st] = heldOver.split('|');
    const occ = gameAt.get(heldOver);
    if (occ?.id === heldId) return null;
    if (!cellOpen(courtId, d, Number(st))) return { bad: true, text: 'That court is closed at this time.' };
    const cell = legality?.[heldOver];
    if (cell?.state === 'blocked') return { bad: true, text: cell.reason || 'That move is not possible.' };
    const base = occ
      ? `Swap with ${formatGameNumber(occ) || 'game'} · ${occ.team_a_name} v ${occ.team_b_name}`
      : 'Move here';
    return { bad: false, text: cell?.state === 'discouraged' && cell.reason ? `${base}. Think twice: ${cell.reason}` : base };
  }, [heldId, heldOver, gameAt, cellOpen, legality]);
  useEffect(() => { if (heldId && !heldGame) putDown(); }, [heldId, heldGame, putDown]);



  // ─── Drops ─────────────────────────────────────────────────────────────
  /**
   * Patch a set of games in local state. The grid never remounts: the day
   * selector, filters and scroll position are all untouched by a drop.
   */
  const patchGames = (patches: Record<string, any>) =>
    setData((prev: any) => {
      if (!prev) return prev;
      let changed = false;
      const nextGames = prev.games.map((g: any) => {
        if (!patches[g.id]) return g;
        changed = true;
        return { ...g, ...patches[g.id] };
      });
      return changed ? { ...prev, games: nextGames } : prev;
    });

  const applyDrop = async (
    payload: Record<string, unknown>,
    optimistic: Record<string, any>,
    onApplied?: () => void,
  ) => {
    const ids = Object.keys(optimistic);
    const before: Record<string, any> = {};
    for (const g of games) if (ids.includes(g.id)) before[g.id] = { ...g };

    patchGames(optimistic); // move it now — roll back only if the server refuses
    setBusy(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'grid_drop', event_id: eventId, ...payload },
      });
      if (error || !res?.ok || res.data?.applied === false) {
        patchGames(before);
        toast.error(
          res?.data?.blocked || error?.message || res?.error?.message || 'That move is not possible.',
        );
        return;
      }

      // Patch whatever `moved` reports straight into local state. When the
      // server returns bare ids the optimistic patch already IS that state,
      // so there is nothing left to reconcile — and no refetch at all.
      const moved: any[] = res.data?.moved ?? [];
      const movedIds = moved.map((m) => (typeof m === 'string' ? m : m?.id)).filter(Boolean);
      const rowPatches: Record<string, any> = {};
      for (const m of moved) if (m && typeof m === 'object' && m.id) rowPatches[m.id] = m;
      if (Object.keys(rowPatches).length) patchGames(rowPatches);

      const list: any[] = res.data?.warnings ?? [];
      setWarnings((prev) => {
        const next = { ...prev };
        for (const id of (movedIds.length ? movedIds : ids)) delete next[id];
        for (const w of list) next[w.game_id] = [...(next[w.game_id] ?? []), w.message];
        return next;
      });
      // Ring the cell the game landed in, so "I don't know where I put that"
      // is answered by the board itself.
      flashGame((movedIds.length ? movedIds : ids)[0]);
      onApplied?.();
      void refreshMoveHead();
      // The colour on the board follows the board: a drop into a clashing cell
      // goes red without anyone pressing Run check.
      void loadCheck();
      // The move is already written; the team summary should not wait for a
      // reload to agree with the board.
      announceScheduleChange(eventId);
    } finally { setBusy(false); }
  };

  /**
   * Undo is not Restore. The stack lives on the server, one entry per
   * placement change on this schedule version, so it survives a reload and
   * the button can name the move before it is clicked.
   */
  const refreshMoveHead = useCallback(async () => {
    const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'move_log_head', event_id: eventId },
    });
    if (res?.ok) setMoveHead({ undo: res.data?.undo ?? null, redo: res.data?.redo ?? null });
  }, [eventId]);

  useEffect(() => { void refreshMoveHead(); }, [refreshMoveHead]);

  const runMove = useCallback(async (direction: 'undo' | 'redo', force = false) => {
    setBusy(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: direction === 'undo' ? 'move_undo' : 'move_redo', event_id: eventId, force },
      });
      if (error || !res?.ok) {
        toast.error(error?.message || res?.error?.message || 'That could not be undone.');
        return;
      }
      if (res.data?.needs_confirmation) {
        toast(res.data.message, {
          action: { label: 'Do it', onClick: () => void runMove(direction, true) },
        });
        return;
      }
      if (res.data?.applied === false) {
        toast.error(res.data?.blocked || 'That could not be undone.');
        return;
      }
      toast.success(res.data?.message ?? 'Done.');
      await load();
      flashGame(res.data?.moved?.[0] ?? null);
      await refreshMoveHead();
      void loadCheck();
      announceScheduleChange(eventId);
    } finally { setBusy(false); }
  }, [eventId, load, refreshMoveHead, loadCheck, flashGame]);

  // Ctrl/Cmd+Z undoes, Shift adds redo — the same two calls as the buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      e.preventDefault();
      if (e.shiftKey) { if (moveHead.redo) void runMove('redo'); return; }
      if (moveHead.undo?.undoable) void runMove('undo');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moveHead, runMove]);

  /**
   * The optimistic patch always carries the DESTINATION date. The server then
   * re-resolves the session from (court, date, start) — a cross-day drop can
   * never inherit the origin day's session.
   */
  const placementPatch = (courtId: string, cellDate: string, start: number) => ({
    court_id: courtId,
    scheduled_date: cellDate,
    scheduled_start_time: fromMin(start),
    scheduled_end_time: fromMin(start + gameMinutes),
    game_status: 'scheduled',
    manually_adjusted: true,
  });


  const courtName = (id?: string | null) =>
    courts.find((c) => c.id === id)?.name ?? 'court';
  const gameLabel = (g: any) => `${g?.team_a_name} vs ${g?.team_b_name}`;

  /**
   * A discouraged drop is allowed — but never silently. Offer both ways out:
   * keep it, or put the game back exactly where it came from.
   */
  const offerPutBack = (dragged: any, reason: string) => {
    const fromCourt = dragged.court_id;
    const fromStart = toMin(dragged.scheduled_start_time);
    const fromDate = dragged.scheduled_date;
    toast.warning(reason || 'That move breaks a soft rule.', {
      duration: 12000,
      description: `${gameLabel(dragged)} — moved anyway.`,
      action: {
        label: 'Put it back',
        onClick: () => {
          if (fromCourt && fromStart != null && fromDate) {
            void applyDrop(
              { op: 'move', game_id: dragged.id, court_id: fromCourt, date: fromDate, start_time: fromMin(fromStart) },
              { [dragged.id]: placementPatch(fromCourt, fromDate, fromStart) },
            );

          } else {
            void applyDrop({ op: 'unschedule', game_id: dragged.id }, {
              [dragged.id]: {
                court_id: null, session_id: null, scheduled_date: null,
                scheduled_start_time: null, scheduled_end_time: null,
                game_status: 'unscheduled', manually_adjusted: true,
              },
            });
          }
        },
      },
      cancel: { label: 'Keep it', onClick: () => {} },
    });
  };

  const dropOnCell = (
    id: string,
    courtId: string,
    cellDate: string,
    start: number,
    cell?: { state: string; reason: string },
  ) => {
    const occupant = gameAt.get(`${courtId}|${cellDate}|${start}`);
    if (occupant?.id === id) return;
    const dragged = games.find((x) => x.id === id);
    if (!dragged) return;
    if (cell?.state === 'blocked') {
      toast.error(cell.reason || 'That move is not possible.');
      return;
    }
    const discouraged = cell?.state === 'discouraged' ? { ...dragged } : null;
    if (occupant) {
      const from = toMin(dragged.scheduled_start_time);
      const fromCourt = dragged.court_id;
      const fromDate = dragged.scheduled_date;
      void applyDrop(
        { op: 'swap', game_id: id, swap_with_game_id: occupant.id, court_id: courtId, date: cellDate, start_time: fromMin(start) },
        {
          [id]: placementPatch(courtId, cellDate, start),
          // The displaced game inherits the dragged game's ORIGIN cell — its
          // own day included, which is the point in All Days.
          [occupant.id]: fromCourt && from != null && fromDate
            ? placementPatch(fromCourt, fromDate, from)
            : { manually_adjusted: true },
        },
        () => {
          // Two cells changed at once — say so plainly.
          toast.success('Games swapped', {
            description:
              `${gameLabel(dragged)} → ${courtName(courtId)} at ${formatTime(fromMin(start))}` +
              (fromCourt && from != null
                ? ` · ${gameLabel(occupant)} → ${courtName(fromCourt)} at ${formatTime(fromMin(from))}`
                : ` · ${gameLabel(occupant)} → unplaced`),
          });
          if (discouraged) offerPutBack(discouraged, cell!.reason);
        },
      );
      return;
    }
    void applyDrop(
      { op: 'move', game_id: id, court_id: courtId, date: cellDate, start_time: fromMin(start) },
      { [id]: placementPatch(courtId, cellDate, start) },
      discouraged ? () => offerPutBack(discouraged, cell!.reason) : undefined,
    );
  };



  const dropOnTray = (id: string) => {
    const g = games.find((x) => x.id === id);
    if (!g?.court_id) return;
    void applyDrop({ op: 'unschedule', game_id: id }, {
      [id]: {
        court_id: null, session_id: null, scheduled_date: null,
        scheduled_start_time: null, scheduled_end_time: null,
        game_status: 'unscheduled', manually_adjusted: true,
      },
    });
  };

  // ─── Click-to-move ─────────────────────────────────────────────────────
  // Ends in dropOnCell — the very function a drag release calls.
  const dropHeldOn = (courtId: string, cellDate: string, start: number, open: boolean) => {
    const id = heldId;
    if (!id || busy) return;
    const occupant = gameAt.get(`${courtId}|${cellDate}|${start}`);
    if (occupant?.id === id) return;
    if (!open) { toast.error('That court is closed at this time.'); return; }
    const cell = legality?.[`${courtId}|${cellDate}|${start}`];
    if (cell?.state === 'blocked') {
      // Same sentence the drag gives; the game stays held.
      toast.error(cell.reason || 'That move is not possible.');
      return;
    }
    putDown();
    dropOnCell(id, courtId, cellDate, start, cell);
  };

  const handleDragEnd = (event: any) => {
    const id = String(event.active?.id ?? '');
    const cells = legality;
    lastDragEndAt.current = Date.now();
    setActiveId(null);
    setOverId(null);
    setLegality(null);
    // Cleared before anything else can happen, so a refused drop leaves the
    // board as clean as a successful one.
    setDragMark(null);
    const over = event.over?.id ? String(event.over.id) : null;
    if (!id || !over) return;
    if (over === 'tray') { dropOnTray(id); return; }
    if (over.startsWith('cell:')) {
      // cell:<courtId>:<yyyy-mm-dd>:<startMinutes>
      const [, courtId, cellDate, start] = over.split(':');
      dropOnCell(id, courtId, cellDate, Number(start), cells?.[`${courtId}|${cellDate}|${Number(start)}`]);
    }

  };




  const warningCount = useMemo(
    () => dayGames.reduce((n, g) => n + (warnings[g.id]?.length ?? 0), 0), [dayGames, warnings]);

  // ─── What is wrong, where ──────────────────────────────────────────────
  // gameId → the publish check's own lines. Nothing else colours a cell.
  const issues = useMemo(() => buildIssueMap(check), [check]);
  const issueOf = useCallback((id: string) => issueLevel(issues[id]), [issues]);
  const backToBackGroup = useMemo(
    () => (check?.warnings ?? []).find((w: any) => w?.kind === 'back_to_back') ?? null,
    [check],
  );
  const backToBackOpen = Number(backToBackGroup?.count ?? backToBackGroup?.items?.length ?? 0);
  const approvedB2B: any[] = Array.isArray(check?.approved_back_to_back) ? check.approved_back_to_back : [];
  // Total pairs, approved or not; the approved share is named beside it.
  const backToBackCount = backToBackOpen + approvedB2B.length;
  /** gameId → the open back-to-back pairs it is part of. */
  const b2bByGame = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const it of backToBackGroup?.items ?? []) {
      for (const id of [it?.game_a_id, it?.game_b_id]) if (id) (m[id] ??= []).push(it);
    }
    return m;
  }, [backToBackGroup]);
  /** gameIds carrying an amber warning of any kind other than back-to-back. */
  const nonB2BWarnIds = useMemo(() => {
    const set = new Set<string>();
    for (const w of check?.warnings ?? []) {
      if (w?.kind === 'back_to_back') continue;
      for (const item of w?.items ?? []) {
        const rows = Array.isArray(item?.games) ? item.games : item?.game_id ? [item] : [];
        for (const r of rows) if (r?.game_id) set.add(String(r.game_id));
      }
    }
    return set;
  }, [check]);
  /**
   * The grid's single matcher. Dropdowns (division, pool, team, non-crossover type)
   * AND together; the Show-only switches OR together (any of them) and AND with the dropdowns.
   */
  /** Dropdowns only — these narrow the board. */
  const dropdownMatch = useCallback((g: any) => {
    if (fDivision !== 'all' && g.division !== fDivision) return false;
    if (fPool !== 'all' && g.pool_name !== fPool) return false;
    if (fType !== 'all' && g.game_type !== fType) return false;
    if (fTeam !== 'all' && g.team_a_registration_id !== fTeam && g.team_b_registration_id !== fTeam) return false;
    return true;
  }, [fDivision, fPool, fType, fTeam]);
  const visibleOnGrid = useCallback((g: any) => {
    if (!dropdownMatch(g)) return false;
    if (!showOnlyActive) return true;
    if (fRequests && g.has_request) return true;
    if (fCrossovers && g.game_type === 'crossover') return true;
    if (fErrors && issues[g.id]?.errors?.length) return true;
    if (fWarnings && nonB2BWarnIds.has(String(g.id))) return true;
    if (fB2B && b2bByGame[g.id]?.length) return true; // open pairs only; approved excluded
    return false;
  }, [dropdownMatch, showOnlyActive, fRequests, fCrossovers, fErrors, fWarnings, fB2B, issues, nonB2BWarnIds, b2bByGame]);
  const placedGames = useMemo(
    () => games.filter((g: any) => g.court_id && g.scheduled_date && g.scheduled_start_time), [games]);
  const shownCount = useMemo(
    () => placedGames.filter(visibleOnGrid).length, [placedGames, visibleOnGrid]);
  const clearShowOnly = () => { setFErrors(false); setFWarnings(false); setFB2B(false); setFRequests(false); setFCrossovers(false); };
  const showOnlyVerb = (() => {
    const parts = [fErrors && 'errors', fWarnings && 'warnings', fB2B && 'unapproved back-to-back',
      fRequests && 'scheduling requests'].filter(Boolean) as string[];
    const has = parts.length ? `have ${parts.length === 1 ? parts[0] : parts.slice(0, -1).join(', ') + ' or ' + parts[parts.length - 1]}` : '';
    if (!fCrossovers) return has;
    return has ? `${has} or are crossovers` : 'are crossovers';
  })();
  const showOnlyLabel = [
    fRequests && 'scheduling requests', fErrors && 'errors', fWarnings && 'warnings',
    fB2B && 'unapproved back-to-back', fCrossovers && 'crossovers',
  ].filter(Boolean) as string[];
  const showOnlyText = showOnlyLabel.length <= 1 ? (showOnlyLabel[0] ?? '')
    : `${showOnlyLabel.slice(0, -1).join(', ')} and ${showOnlyLabel[showOnlyLabel.length - 1]}`;
  const approveB2B = useCallback(async (it: any) => {
    const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'b2b_approve', event_id: eventId,
        registration_id: it.registration_id, game_a_id: it.game_a_id, game_b_id: it.game_b_id,
      },
    });
    if (res?.ok) toast.success(`Back-to-back approved for ${it.team ?? 'this team'}`);
    else toast.error(res?.error?.message ?? 'Approving failed. Nothing has changed.');
    await loadCheck();
  }, [eventId, loadCheck]);
  const unapproveB2B = useCallback(async (it: any) => {
    const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'b2b_unapprove', event_id: eventId, approval_id: it.approval_id },
    });
    if (res?.ok) toast.success('Approval removed — the warning is back');
    else toast.error(res?.error?.message ?? 'Removing the approval failed. Nothing has changed.');
    await loadCheck();
  }, [eventId, loadCheck]);

  /** Every placed game in the report, in board order — the whole event. */
  const allBadGames = useMemo(() => {
    const list = games.filter((g: any) =>
      g.court_id && g.scheduled_date && g.scheduled_start_time && issues[g.id]);
    const rank = (g: any) => (issues[g.id]?.errors?.length ? 0 : 1);
    return list.sort((a: any, b: any) =>
      rank(a) - rank(b) ||
      String(a.scheduled_date).localeCompare(String(b.scheduled_date)) ||
      String(a.scheduled_start_time).localeCompare(String(b.scheduled_start_time)) ||
      String(a.court_id).localeCompare(String(b.court_id)));
  }, [games, issues]);

  /**
   * The arrows walk exactly the coloured cells you can see. The counter must
   * agree with the board, so it is scoped to the day in view, and the cells on
   * the other days are named beside it rather than silently left out.
   */
  const badGames = useMemo(
    () => allBadGames.filter((g: any) => viewDateSet.has(g.scheduled_date) && (!filtersActive || visibleOnGrid(g))),
    [allBadGames, viewDateSet, filtersActive, visibleOnGrid]);
  const offViewBad = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of allBadGames) {
      if (viewDateSet.has(g.scheduled_date)) continue;
      if (filtersActive && !visibleOnGrid(g)) continue;
      m.set(g.scheduled_date, (m.get(g.scheduled_date) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [allBadGames, viewDateSet, filtersActive, visibleOnGrid]);

  /** Step to the next / previous bad cell, switching day and scrolling to it. */
  const stepBad = useCallback((delta: number) => {
    if (!badGames.length) return;
    const next = ((badIndex + delta) % badGames.length + badGames.length) % badGames.length;
    setBadIndex(next);
    const g = badGames[next];
    if (date !== ALL_DAYS && g.scheduled_date !== date) setDate(g.scheduled_date);
    setFlaggedId(g.id);
    window.setTimeout(() => {
      document.getElementById(`grid-game-${g.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    window.setTimeout(() => setFlaggedId((cur) => (cur === g.id ? null : cur)), 6000);
  }, [badGames, badIndex, date]);

  /**
   * A row in the check panel is a link to a cell: switch to that game's day,
   * scroll it into view and ring it — the same behaviour as the ‹ › arrows.
   */
  const focusCheckRow = useCallback((row: { game_id?: string | null; date?: string | null }) => {
    const id = row?.game_id ? String(row.game_id) : null;
    if (!id) return;
    if (row.date && date !== ALL_DAYS && row.date !== date) setDate(row.date);
    setFlaggedId(id);
    window.setTimeout(() => {
      document.getElementById(`grid-game-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    window.setTimeout(() => setFlaggedId((cur) => (cur === id ? null : cur)), 6000);
  }, [date]);

  // ─── A finding, marked on the board ─────────────────────────────────────
  // Clicking a finding in the list marks exactly the games in its `games`
  // array (ids the server already sent), takes the board to the first one and
  // lets ‹ › walk the rest. Same finding again, or Escape, clears it.
  const [marked, setMarked] = useState<{ key: string; title: string; ids: string[] } | null>(null);
  const [markIndex, setMarkIndex] = useState(0);
  const markedSet = useMemo(() => new Set(marked?.ids ?? []), [marked]);
  const markedGames = useMemo(() => {
    if (!marked) return [] as any[];
    const byId = new Map(games.map((g: any) => [String(g.id), g]));
    return marked.ids.map((id) => byId.get(id)).filter(Boolean).sort((a: any, b: any) =>
      String(a.scheduled_date ?? '').localeCompare(String(b.scheduled_date ?? '')) ||
      String(a.scheduled_start_time ?? '').localeCompare(String(b.scheduled_start_time ?? '')) ||
      String(a.court_id ?? '').localeCompare(String(b.court_id ?? '')));
  }, [marked, games]);
  const markedDays = useMemo(
    () => [...new Set(markedGames.map((g: any) => g.scheduled_date).filter(Boolean))].sort(),
    [markedGames]);
  // The board scrolls inside its own container (not the window), and rows far
  // from view are virtualised away — so we scroll the container by row maths
  // first, then correct against the rendered card, clear of sticky headers.
  const [scrollTarget, setScrollTarget] = useState<{ id: string; n: number } | null>(null);
  const goToMarked = useCallback((list: any[], i: number) => {
    const g = list[i];
    if (!g) return;
    setMarkIndex(i);
    if (g.scheduled_date && date !== ALL_DAYS && g.scheduled_date !== date) setDate(g.scheduled_date);
    setScrollTarget({ id: String(g.id), n: Date.now() });
  }, [date]);
  useEffect(() => {
    if (!scrollTarget) return;
    const sc = scrollRef.current;
    if (!sc) return;
    const g = games.find((x: any) => String(x.id) === scrollTarget.id);
    if (!g) return;
    let cancelled = false;
    const settle = (attempt: number) => {
      if (cancelled) return;
      const el = document.getElementById(`grid-game-${g.id}`);
      if (!el) {
        // Not rendered yet (virtualised): jump by row arithmetic, then retry.
        const gm = toMin(g.scheduled_start_time);
        let idx = -1;
        flatRows.forEach((r: any, k: number) => {
          if (r.kind !== 'day' && r.date === g.scheduled_date && r.start <= gm) idx = k;
        });
        if (idx >= 0) {
          const nextTop = Math.max(0, idx * ROW_HEIGHT - sc.clientHeight / 2 + ROW_HEIGHT);
          sc.scrollTop = nextTop;
          if (virtualize) setScrollTop(nextTop);
        }
        if (attempt < 10) window.setTimeout(() => settle(attempt + 1), 60);
        return;
      }
      const scR = sc.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      // Sticky header rows and the sticky time column cover part of the view.
      const stickyTop = [...sc.querySelectorAll<HTMLElement>('[class*="sticky"]')]
        .filter((s) => s.getBoundingClientRect().top - scR.top < 60)
        .reduce((m, s) => Math.max(m, s.getBoundingClientRect().bottom - scR.top), 0);
      const timeCol = (sc.querySelector('[data-time-col]') as HTMLElement | null)?.getBoundingClientRect().width ?? 72;
      const visH = sc.clientHeight - stickyTop;
      const visW = sc.clientWidth - timeCol;
      const top = sc.scrollTop + (r.top - scR.top) - stickyTop - Math.max(8, (visH - r.height) / 2);
      const left = sc.scrollLeft + (r.left - scR.left) - timeCol - Math.max(8, (visW - r.width) / 2);
      const nextTop = Math.max(0, top);
      const nextLeft = Math.max(0, left);
      // Set the board scroller itself, synchronously. Repeating briefly after
      // the findings dialog closes prevents focus restoration from undoing the
      // jump and also lets virtualised rows settle before the final correction.
      sc.scrollTop = nextTop;
      sc.scrollLeft = nextLeft;
      if (virtualize) setScrollTop(nextTop);
      if (attempt < 5) window.setTimeout(() => settle(attempt + 1), 80);
    };
    const t = window.setTimeout(() => settle(0), 80);
    return () => { cancelled = true; window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollTarget, date]);
  const markFinding = useCallback((key: string, title: string, rows: CheckRow[]) => {
    if (marked?.key === key) { setMarked(null); return; }
    const ids = [...new Set((rows ?? []).map((r) => (r?.game_id ? String(r.game_id) : '')).filter(Boolean))];
    if (!ids.length) return;
    setMarked({ key, title, ids });
    const byId = new Map(games.map((g: any) => [String(g.id), g]));
    const list = ids.map((id) => byId.get(id)).filter(Boolean).sort((a: any, b: any) =>
      String(a.scheduled_date ?? '').localeCompare(String(b.scheduled_date ?? '')) ||
      String(a.scheduled_start_time ?? '').localeCompare(String(b.scheduled_start_time ?? '')) ||
      String(a.court_id ?? '').localeCompare(String(b.court_id ?? '')));
    goToMarked(list, 0);
  }, [marked, games, goToMarked]);
  const stepMarked = useCallback((delta: number) => {
    if (!markedGames.length) return;
    const n = markedGames.length;
    goToMarked(markedGames, ((markIndex + delta) % n + n) % n);
  }, [markedGames, markIndex, goToMarked]);
  useEffect(() => {
    if (!marked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !heldId && !checkOpen) setMarked(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [marked, heldId, checkOpen]);



  if (loading) {
    return <Card><CardContent className="pt-6 space-y-3">
      <Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" />
    </CardContent></Card>;
  }

  if (!data) {
    return <Card><CardContent className="pt-6 text-sm text-muted-foreground">
      No draft schedule yet — generate one first.
    </CardContent></Card>;
  }

  /** Props for one card — the component itself lives at module scope. */
  const cardProps = (g: any, inTray = false) => ({
    g,
    inTray,
    flagged: flaggedId === g.id,
    marked: markedSet.has(String(g.id)),
    markedCurrent: markedGames[markIndex]?.id === g.id,
    // Toggles emphasise (dim, still fully live); dropdowns keep their old behaviour.
    dim: !inTray && (showOnlyActive ? dropdownMatch(g) && !visibleOnGrid(g) : baseFiltersActive && !visibleOnGrid(g)),
    hidden: !inTray && showOnlyActive && !dropdownMatch(g),
    warns: warnings[g.id] ?? NO_WARNS,
    disabled: busy,
    color: divisionColors[g.division] ?? null,
    issue: issueOf(g.id),
    issueDetails: issues[g.id] ?? null,
    courtLabel: g.court_id ? courtName(g.court_id) : null,
    onEnter: onCardEnter,
    onLeave: onCardLeave,
    onDown: clearHover,
    b2bPairs: b2bByGame[g.id] ?? null,
    onApproveB2B: approveB2B,
    moveMode: !!activeId || !!heldId,
    // Click-to-move lives on placed grid cards only.
    selected: !inTray && selectedId === g.id,
    held: !inTray && heldId === g.id,
    holdLocked: !inTray && !!heldId && heldId !== g.id,
    onSelect: inTray ? undefined : selectCard,
    onToggleMove: inTray ? undefined : toggleMove,
  });

  const hoveredGame = hoverId && !activeId && !busy ? games.find((g: any) => g.id === hoverId) : null;





  const venueGroups: Array<{ venue: string | null; courts: any[] }> = [];
  for (const c of courts) {
    const last = venueGroups[venueGroups.length - 1];
    if (last && last.venue === (c.venue_name ?? null)) last.courts.push(c);
    else venueGroups.push({ venue: c.venue_name ?? null, courts: [c] });
  }
  const multiVenue = new Set(courts.map((c) => c.venue_id)).size > 1;

  // Wider columns: a court column has to fit a full team name, not an ellipsis.
  const gridTemplate = `92px repeat(${Math.max(1, courts.length)}, minmax(196px, 1fr))`;

  const activeGame = activeId ? games.find((g: any) => g.id === activeId) : null;

  // The game that would be displaced if the drag were released right now.
  let displaced: any = null;
  if (activeId && overId?.startsWith('cell:')) {
    const [, oCourt, oDate, oStart] = overId.split(':');
    const occ = gameAt.get(`${oCourt}|${oDate}|${Number(oStart)}`);
    if (occ && occ.id !== activeId) displaced = occ;
  }



  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      autoScroll={false}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={(e) => {
        const ae: any = e.activatorEvent;
        const t = ae?.touches?.[0];
        if (t) dragPtr.current = { x: t.clientX, y: t.clientY, touch: true };
        else if (ae && 'clientX' in ae) dragPtr.current = { x: ae.clientX, y: ae.clientY, touch: false };
        // FIRST line: the hover detail must be gone before the drag overlay
        // mounts and the source card unmounts. A useEffect would be too late.
        setHoverId(null);
        // A drag always wins over a held or selected game.
        setHeldId(null);
        setSelectedId(null);
        const id = String(e.active.id);
        // Legality for every visible slot, worked out ONCE, here — never on hover.
        setLegality(computeLegality(id));
        // Sight, not rules: mark the rest of these two teams' weekend. Keyboard
        // dragging comes through the very same hook, so it gets it too.
        setDragMark(companionMark(
          teamGameIndex,
          games.find((x: any) => x.id === id),
          (d) => formatCalendarDate(d, 'EEE d MMM', d),
        ));
        setActiveId(id);
        // The tray is also the unschedule target, so it opens itself the
        // moment a game is in the air.
        setTrayOpen(true);
      }}
      onDragOver={(e) => setOverId(e.over ? String(e.over.id) : null)}
      onDragCancel={() => { setHoverId(null); setActiveId(null); setOverId(null); setLegality(null); setDragMark(null); }}
      onDragEnd={handleDragEnd}
    >
    <>

    {/*
      Fullscreen is a className swap on ONE wrapper that always exists. The
      Card, the scroll container and every card below stay mounted, so the
      scroll position and both filter rows survive the toggle — and no data
      is refetched on the way in or out.
    */}
    <div className={filled ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : ''}>
    <Card className={filled ? 'flex min-h-0 flex-1 flex-col rounded-none border-0 shadow-none' : ''}>
      <CardContent className={`${filled ? 'px-4 pt-3' : 'pt-6'} space-y-3${filled ? ' flex min-h-0 flex-1 flex-col' : ''}`}>

        {/* Day selector + the grid's own actions. This row sticks to the top
            of the viewport while the board scrolls: Undo has to be reachable
            the moment a game lands in the wrong cell, not 600px further down. */}
        <div className={`sticky top-0 z-30 flex flex-col items-stretch gap-2 border-b bg-background/95 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 ${filled ? '-mx-4 -mt-3 px-4' : '-mx-6 -mt-6 px-6'}`}>
          <div className="flex flex-wrap items-center gap-2">
          {(data.dates ?? []).map((d: string) => (
            <Button
              key={d} size="sm" variant={d === date ? 'default' : 'outline'}
              onClick={() => { setDate(d); void load(d); }}
            >
              {formatCalendarDate(d, 'EEE MMM d', d)}
            </Button>
          ))}
          {(data.dates ?? []).length > 1 && (
            <Button
              size="sm"
              variant={allDays ? 'default' : 'outline'}
              onClick={() => setDate(ALL_DAYS)}
              title="Every day on one canvas — drag a game from one day to another"
            >
              <CalendarRange className="mr-1.5 h-3.5 w-3.5" /> All days
            </Button>
          )}
          {/* Marked games on a day that is not on screen are counted here
              rather than silently showing nothing. */}
          {dragMark && [...dragMark.byDate.entries()]
            .filter(([d]) => !viewDateSet.has(d))
            .map(([d, n]) => (
              <span
                key={`offday-${d}`}
                data-testid={`drag-offday-${d}`}
                className="rounded bg-foreground px-2 py-1 text-xs font-semibold text-background"
              >
                {formatCalendarDate(d, 'EEE MMM d', d)} · {n} more
              </span>
            ))}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {marked && (
              <span
                data-testid="marked-finding"
                className="flex flex-wrap items-center gap-1.5 rounded border-2 border-foreground bg-card px-2 py-0.5 text-xs"
              >
                <span className="font-semibold">{marked.title}</span>
                <span data-testid="marked-count">— {markedGames.length} game{markedGames.length === 1 ? '' : 's'}</span>
                {markedDays.length > 1 && (
                  <span className="text-muted-foreground" data-testid="marked-days">
                    · on {markedDays.length} days ({markedDays.map((d) => formatCalendarDate(d, 'EEE', d)).join(', ')})
                  </span>
                )}
                {markedGames.length > 1 && (
                  <>
                    <Button size="sm" variant="outline" className="h-6 w-6 p-0" aria-label="Previous game in this problem"
                      data-testid="marked-prev" onClick={() => stepMarked(-1)}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="tabular-nums" data-testid="marked-index">{markIndex + 1} of {markedGames.length}</span>
                    <Button size="sm" variant="outline" className="h-6 w-6 p-0" aria-label="Next game in this problem"
                      data-testid="marked-next" onClick={() => stepMarked(1)}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" data-testid="marked-clear"
                  onClick={() => setMarked(null)} title="Clear (Esc)">
                  Clear
                </Button>
              </span>
            )}
            {(check?.error_count > 0 || check?.warning_count > 0) && (
              <span className="flex items-center gap-2">
                {/* Each number opens the list filtered to that kind. */}
                {check?.error_count > 0 && (
                  <button
                    type="button"
                    data-testid="check-counter"
                    className={`flex items-center gap-1.5 rounded px-1 py-0.5 font-medium text-red-700 hover:bg-muted ${fErrors ? 'bg-red-100 ring-2 ring-red-600' : ''}`}
                    title="Highlight the games with an error"
                    aria-pressed={fErrors}
                    onClick={() => setFErrors((v) => !v)}
                  >
                    <Circle className="h-3.5 w-3.5 fill-red-600 text-red-700" />
                    {check.error_count} error{check.error_count === 1 ? '' : 's'}
                  </button>
                )}
                {check?.warning_count > 0 && (
                  <button
                    type="button"
                    data-testid="check-counter-warnings"
                    className={`flex items-center gap-1.5 rounded px-1 py-0.5 text-amber-700 hover:bg-muted ${fWarnings ? 'bg-amber-100 ring-2 ring-amber-600' : ''}`}
                    title="Highlight the games with a warning"
                    aria-pressed={fWarnings}
                    onClick={() => setFWarnings((v) => !v)}
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {check.warning_count} warning{check.warning_count === 1 ? '' : 's'}
                  </button>
                )}

                {(backToBackCount > 0) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid="back-to-back-counter"
                    className={`h-auto gap-1.5 px-1 py-0.5 font-medium text-amber-700 hover:bg-muted hover:text-amber-700 ${fB2B ? 'bg-amber-100 ring-2 ring-amber-600' : ''}`}
                    aria-pressed={fB2B}
                    onClick={() => setFB2B((v) => !v)}
                    title="Highlight the unapproved back-to-back games"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {backToBackCount} back-to-back
                    {approvedB2B.length > 0 && <span className="font-normal">, {approvedB2B.length} approved</span>}
                  </Button>
                )}
                <button
                  type="button"
                  data-testid="show-check-list"
                  className="text-xs text-primary underline underline-offset-2 hover:no-underline"
                  onClick={() => { setCheckFocusKind(null as any); setCheckOpen(true); }}
                >Show the list</button>
                {badGames.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Button
                      size="sm" variant="outline" className="h-7 w-7 p-0"
                      aria-label="Previous cell with a problem"
                      title="Previous cell with a problem"
                      onClick={() => stepBad(-1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {Math.min(badIndex, badGames.length - 1) + 1} of {badGames.length} cell
                      {badGames.length === 1 ? '' : 's'}
                      {offViewBad.length > 0 && (
                        <> · {offViewBad.map(([d, n]) =>
                          `${n} on ${formatCalendarDate(d, 'EEE MMM d', d)}`).join(' · ')}</>
                      )}
                    </span>
                    <Button
                      size="sm" variant="outline" className="h-7 w-7 p-0"
                      aria-label="Next cell with a problem"
                      title="Next cell with a problem"
                      onClick={() => stepBad(1)}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {/*
              The publish check's counts, on the board itself, with arrows that
              walk from one coloured cell to the next so nobody hunts across a
              two-day, eight-court grid.
            */}
            {warningCount > 0 && (
              <span className="flex items-center gap-1.5 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                {warningCount} warning{warningCount === 1 ? '' : 's'} {allDays ? 'in view' : 'on this day'}
              </span>
            )}
            {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {/*
              A plain button, never a menu: undo is what a director reaches for
              after a mistake, so it must work on the first click. It names the
              move before it happens, and says plainly when the last change was
              too big for undo.
            */}
            {/* Always rendered, greyed when the stack is empty: a safety net
                nobody can see is a safety net nobody trusts. */}
            {/* The safety net, in amber: it must not compete with the red
                error cells on the board or with the blue Place games button,
                and it must never disappear. */}
            {/* Save · History · Undo — the three ways to move the board
                through time, on one line and in one colour. */}
            <div className="flex items-center">
              {/* "Do I have to save this?" — no. Every move is written as it
                  lands, so the board says so out loud, and the button next to
                  it is named for what it actually does: keep a copy. */}
              <span
                data-testid="autosave-status"
                className="mr-2 whitespace-nowrap text-xs text-muted-foreground"
              >
                {busy ? 'Saving…' : 'Every move saves itself'}
              </span>
              <div className="flex items-center gap-1">
                {onSaveArrangement && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onSaveArrangement}
                    title="Your moves are already saved. This keeps a named copy of the whole board so you can come back to it."
                    className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                  >
                    <Save className="mr-1.5 h-3.5 w-3.5" /> Keep a copy
                  </Button>
                )}
                {onOpenHistory && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onOpenHistory}
                    title="Every change and every saved arrangement, newest first"
                    className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                  >
                    <HistoryIcon className="mr-1.5 h-3.5 w-3.5" /> History
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || !moveHead.undo?.undoable}
                  onClick={() => void runMove('undo')}
                  title={`Undo takes back one move. Use History to rewind the whole board.${moveHead.undo?.undoable ? ' (Ctrl+Z)' : ''}`}
                  className="max-w-[24rem] truncate border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-900 disabled:opacity-60"
                >
                  <Undo2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                  {moveHead.undo ? `Undo — ${moveHead.undo.label}` : 'Undo — nothing to undo yet'}
                </Button>
                {moveHead.redo?.undoable && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void runMove('redo')}
                    title="Redo (Ctrl+Shift+Z)"
                    className="max-w-[20rem] truncate border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                  >
                    <Redo2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    Redo — {moveHead.redo.label}
                  </Button>
                )}
              </div>
            </div>
            {placeControl ?? (onPlaceGames && (
              <Button
                size="sm"
                onClick={onPlaceGames}
                disabled={placeDisabled}
                title="Pick which games, which courts and which hours, then place them"
              >
                <CalendarClock className="mr-1.5 h-3.5 w-3.5" /> {placeLabel}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => void load()} disabled={busy}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
          </div>

          {/* One always-visible, wrapping filter line. Grid and tray retain
              independent values; the segmented control chooses which set the
              controls below are editing. */}
          <div className="flex flex-wrap items-center gap-2" data-schedule-filters>
            <div
              className="flex items-center rounded-md border p-0.5"
              title={filterScope === 'grid'
                ? 'Grid filters dim the games that do not match. Nothing moves.'
                : 'Tray filters hide unplaced games that do not match.'}
            >
              <Button size="sm" className="h-7" variant={filterScope === 'grid' ? 'default' : 'ghost'} onClick={() => setFilterScope('grid')}>Grid</Button>
              <Button size="sm" className="h-7" variant={filterScope === 'tray' ? 'default' : 'ghost'} onClick={() => setFilterScope('tray')}>Tray</Button>
            </div>
            {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
            <Select value={scopeDivision} onValueChange={chooseDivision}>
              <SelectTrigger className="h-8 w-full sm:w-auto sm:min-w-[8rem]" aria-label="Filter division"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All divisions</SelectItem>{divisions.map((d: string) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={scopePool} onValueChange={choosePool}>
              <SelectTrigger className="h-8 w-full sm:w-auto sm:min-w-[7rem]" aria-label="Filter pool"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All pools</SelectItem>{scopePools.map((p: string) => <SelectItem key={p} value={p}>Pool {p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={scopeTeam} onValueChange={setScopeTeam}>
              <SelectTrigger className="h-8 w-full sm:w-auto sm:min-w-[8rem] sm:max-w-[13rem]" aria-label="Filter team"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">{allTeamsLabel}</SelectItem>{scopeTeams.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterScope === 'grid' ? fType : tType} onValueChange={filterScope === 'grid' ? setFType : setTType}>
              <SelectTrigger className="h-8 w-auto min-w-[9rem]" aria-label="Filter game type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Pool &amp; bracket</SelectItem>
                <SelectItem value="pool">Pool only</SelectItem>
                <SelectItem value="crossover">Crossover only</SelectItem>
                <SelectItem value="bracket">Bracket only</SelectItem>
              </SelectContent>
            </Select>
            {filterScope === 'tray' && <div className="flex items-center gap-2 whitespace-nowrap">
              <Switch id={`${filterScope}-requests`} checked={filterScope === 'grid' ? fRequests : tRequests} onCheckedChange={filterScope === 'grid' ? setFRequests : setTRequests} />
              <Label htmlFor={`${filterScope}-requests`} className="text-sm">With a scheduling request</Label>
            </div>}
            {((filterScope === 'grid' && filtersActive) || (filterScope === 'tray' && trayFiltersActive)) && (
              <Button size="sm" variant="ghost" className="h-8" onClick={() => {
                if (filterScope === 'grid') { setFDivision('all'); setFPool('all'); setFTeam('all'); setFType('all'); setFRequests(false); setFErrors(false); setFWarnings(false); setFB2B(false); }
                else { setTDivision('all'); setTPool('all'); setTTeam('all'); setTType('all'); setTRequests(false); }
              }}>Clear</Button>
            )}
            {divisions.map((d: string) => {
              const c = divisionColors[d];
              return (
                <span key={d} className="flex items-center gap-1 text-xs font-semibold" style={{ color: c?.chipText }}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: c?.border }} />{d}
                </span>
              );
            })}
          </div>
          {filterScope === 'grid' && (
            <div data-testid="show-only-row" className="mt-2 flex w-full flex-wrap items-center gap-x-5 gap-y-2">
              <span className="text-sm font-semibold">Highlight:</span>
              {([
                ['grid-requests', 'Scheduling requests', fRequests, setFRequests],
                ['grid-errors', 'Errors', fErrors, setFErrors],
                ['grid-warnings', 'Warnings', fWarnings, setFWarnings],
                ['grid-b2b', 'Back-to-back', fB2B, setFB2B],
                ['grid-crossovers', 'Crossovers', fCrossovers, setFCrossovers],
              ] as [string, string, boolean, (v: boolean) => void][]).map(([id, label, on, set]) => (
                <div key={id} className="flex items-center gap-2 whitespace-nowrap">
                  <Switch id={id} data-testid={id} checked={on} onCheckedChange={set} />
                  <Label htmlFor={id} className="text-sm">{label}</Label>
                </div>
              ))}
            </div>
          )}
          {filterScope === 'grid' && showOnlyActive && (
            <div data-testid="problem-filter-banner" className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-warning bg-warning/15 px-3 py-1.5 text-sm font-medium text-foreground">
              <span>
                {shownCount} of {placedGames.filter(dropdownMatch).length} games {showOnlyVerb}. The rest are dimmed.{baseFiltersActive ? ' (Within the dropdown filters.)' : ''}
              </span>
              <Button size="sm" variant="outline" className="h-7" data-testid="problem-show-everything"
                onClick={clearShowOnly}>Clear</Button>
            </div>
          )}
        </div>

        {/* Both filter sets and the colour key live in the inline row above. */}



        {/* Click-to-move: the bar that says a game is held, which, and what next.
            Fixed, so the board never shifts when it appears. */}
        {heldGame && !activeId && (
          <div
            data-testid="move-bar"
            role="status"
            className="fixed left-1/2 top-3 z-50 flex w-[min(720px,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-2 rounded-md border-2 border-primary bg-card px-3 py-2 text-sm shadow-lg"
          >
            <div className="flex items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="font-semibold">
                  Moving {formatGameNumber(heldGame) || 'game'} · {heldGame.team_a_name} v {heldGame.team_b_name}.
                </span>{' '}
                Click a cell to drop it.
                {heldPreview && (
                  <span data-testid="move-preview" className={`mt-0.5 block text-xs font-medium ${heldPreview.bad ? 'text-destructive' : 'text-foreground'}`}>
                    {heldPreview.text}
                  </span>
                )}
              </span>
              <Button size="sm" variant="outline" onClick={putDown} data-testid="move-cancel">Cancel</Button>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.blocked}`} />Can’t go here</span>
              <span className="flex items-center gap-1.5"><span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.busy}`} />These teams are already playing</span>
              <span className="flex items-center gap-1.5"><span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.discouraged}`} />Allowed, but think twice</span>
              {dragMark && (
                <span className="flex items-center gap-1.5"><span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.companion}`} />{dragMark.message}</span>
              )}
            </div>
          </div>
        )}

        {/* What the shading means while a game is in the air. */}
        {activeId && (
          // Floats over the page instead of sitting in the flow: inserting it
          // above the board pushed every row down mid-drag and slid the first
          // row under the sticky header. Nothing may move when a drag begins.
          <div
            data-testid="drag-legend"
            className="pointer-events-none fixed bottom-4 left-1/2 z-50 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center gap-4 rounded-md border bg-card px-3 py-2 text-xs shadow-lg"
          >
            <span className="font-medium">While dragging:</span>
            <span className="flex items-center gap-1.5">
              <span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.blocked}`} />
              Can’t go here
            </span>
            <span className="flex items-center gap-1.5">
              <span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.busy}`} />
              These teams are already playing
            </span>
            <span className="flex items-center gap-1.5">
              <span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.discouraged}`} />
              Allowed, but think twice
            </span>
            {dragMark && (
              <span className="flex items-center gap-1.5">
                <span className={`h-3 w-5 rounded-sm ${MOVE_CELL_STYLE.companion}`} />
                {dragMark.message}
              </span>
            )}
          </div>
        )}

        {/* The same sentence, spoken. Keyboard dragging lands here too. */}
        <div role="status" aria-live="polite" className="sr-only" data-testid="drag-companions-announcement">
          {dragMark?.message ?? ''}
        </div>

        {/* Unplaced tray — also the unschedule drop target */}
        {/* A strip until it is needed: one unplaced game does not deserve a
            permanent shelf above the board. A drag opens it on its own, so
            drag-here-to-unschedule still works without a click first. */}
        <TrayDrop dragging={!!activeId}>
          <button
            type="button"
            onClick={() => setTrayOpen((v) => !v)}
            aria-expanded={trayOpen}
            className="flex w-full items-center gap-2 text-sm font-medium text-amber-900"
          >
            <Inbox className="h-4 w-4 text-amber-700" />
            Unplaced games
            <Badge variant="secondary">
              {trayFiltersActive ? `${trayGames.length} of ${allTrayGames.length}` : trayGames.length}
            </Badge>
            <ChevronDown
              className={`h-4 w-4 text-amber-700 transition-transform ${trayOpen ? 'rotate-180' : ''}`}
            />
            {trayOpen && (
              <span className="text-xs font-normal text-amber-800/80">
                drag a game here to unschedule it
              </span>
            )}
          </button>
          {trayOpen && (
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {allTrayGames.length === 0
                ? <span className="text-xs text-amber-800/80">Every game has a court and a time.</span>
                : trayGames.length === 0
                  ? <span className="text-xs text-amber-800/80">No unplaced games match the tray filters.</span>
                  : trayGames.map((g) => (
                      <div key={g.id} className="flex w-44 shrink-0 flex-col gap-1">
                        <GameCard {...cardProps(g, true)} />
                        {/* A game sitting here with no explanation is the thing
                            operators cannot act on. Say which rule stopped it. */}
                        {unplacedReasons?.[g.id] && (
                          <p className="text-[11px] leading-snug text-amber-900/80">
                            Not placed: {unplacedReasons[g.id]}
                          </p>
                        )}
                      </div>
                    ))}
            </div>
          )}
        </TrayDrop>


        {/* Grid */}
        {flatRows.length === 0 ? (
          <div className="rounded-lg border p-6 text-sm text-muted-foreground text-center">
            No open court hours {allDays ? 'on any day' : 'on this day'}.
          </div>
        ) : (
          <div
            ref={scrollRef}
            data-testid="schedule-grid-scroller"
            onScroll={(e) => virtualize && setScrollTop((e.target as HTMLDivElement).scrollTop)}
            style={{ maxHeight: filled ? undefined : '70vh' }}
            className={`overflow-auto rounded-lg border${filled ? ' min-h-0 flex-1' : ''}`}
          >
            <div style={{ display: 'grid', gridTemplateColumns: gridTemplate }}>
              {/* Venue header row */}
              {multiVenue && (
                <>
                  <div className="sticky top-0 z-20 bg-muted border-b px-2 py-1" />
                  {venueGroups.map((vg, i) => (
                    <div
                      key={`${vg.venue}-${i}`}
                      style={{ gridColumn: `span ${vg.courts.length}` }}
                      className="sticky top-0 z-20 bg-muted border-b border-l px-2 py-1 text-xs font-semibold truncate"
                    >
                      {vg.venue ?? 'Venue'}
                    </div>
                  ))}
                </>
              )}
              {/* Court header row */}
              <div className={`sticky ${multiVenue ? 'top-[25px]' : 'top-0'} z-20 bg-background border-b px-2 py-1.5 text-xs font-medium`}>
                Time
              </div>
              {courts.map((c) => (
                <div
                  key={c.id}
                  className={`sticky ${multiVenue ? 'top-[25px]' : 'top-0'} z-20 bg-background border-b border-l px-2 py-1.5 text-xs font-medium truncate`}
                >
                  {c.name}
                </div>
              ))}

              {/* Virtualisation spacer above */}
              {virtualize && firstRow > 0 && (
                <div style={{ gridColumn: '1 / -1', height: firstRow * ROW_HEIGHT }} />
              )}

              {visibleRows.map((row) => (
                row.kind === 'day' ? (
                  // A day banner is a full-width row of the same height, which
                  // keeps the virtualisation arithmetic uniform.
                  <div
                    key={`day-${row.date}`}
                    style={{ gridColumn: '1 / -1', height: ROW_HEIGHT }}
                    className="flex items-center gap-2 border-b border-t bg-primary/10 px-3 text-sm font-semibold"
                  >
                    <CalendarRange className="h-4 w-4 text-primary" />
                    {formatCalendarDate(row.date, 'EEEE MMM d', row.date)}
                  </div>
                ) : (
                  <div key={`${row.date}-${row.start}`} style={{ display: 'contents' }}>
                    <div
                      data-time-col
                      className={`border-b px-2 py-1 text-[11px] ${
                        dragMark?.rowKeys.has(`${row.date}|${row.start}`)
                          ? 'bg-foreground/[0.10] font-semibold text-foreground'
                          : 'bg-background text-muted-foreground'
                      }`}
                      style={{ height: ROW_HEIGHT }}
                    >
                      {formatTime(fromMin(row.start))}
                    </div>
                    {courts.map((c) => {
                      const key = `${c.id}|${row.date}|${row.start}`;
                      const open = cellOpen(c.id, row.date, row.start);
                      const g = gameAt.get(key);
                      return (
                        <DropCell
                          key={c.id}
                          id={`cell:${c.id}:${row.date}:${row.start}`}
                          open={open}
                          dragging={!!activeId || !!heldId}
                          occupied={!!g && g.id !== (activeId ?? heldId)}
                          legality={legality?.[key]}
                          issue={g ? issueOf(g.id) : null}
                          marked={!!g && !!dragMark?.gameIds.has(g.id)}
                          rowMarked={!!dragMark?.rowKeys.has(`${row.date}|${row.start}`)}
                          height={ROW_HEIGHT}
                          holding={!!heldId}
                          holdsHeld={!!g && g.id === heldId}
                          onHeldClick={() => dropHeldOn(c.id, row.date, row.start, open)}
                          onHeldEnter={() => setHeldOver(key)}
                          swapLabel={g && g.id !== (activeId ?? heldId) ? `Swap with ${formatGameNumber(g) || 'game'} · ${g.team_a_name} v ${g.team_b_name}` : null}
                        >
                          {g ? <GameCard {...cardProps(g)} /> : null}
                        </DropCell>
                      );
                    })}
                  </div>
                )
              ))}

              {/* Virtualisation spacer below */}
              {virtualize && lastRow < flatRows.length && (
                <div style={{ gridColumn: '1 / -1', height: (flatRows.length - lastRow) * ROW_HEIGHT }} />
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </div>


    {/* The one and only hover detail for the whole grid. */}
    <HoverDetail
      g={hoveredGame}
      warns={hoveredGame ? warnings[hoveredGame.id] ?? [] : []}
      issues={hoveredGame ? issues[hoveredGame.id] ?? null : null}
      requests={hoveredGame ? requestsForGame(hoveredGame) : []}
      courtName={hoveredGame ? courts.find((c) => c.id === hoveredGame.court_id)?.name ?? null : null}
      venueName={hoveredGame ? courts.find((c) => c.id === hoveredGame.court_id)?.venue_name ?? null : null}
      posRef={hoverPos}
    />

    {/* Everything wrong with this schedule, in one place, from the same report
        that colours the cells. */}
    <ScheduleCheckPanel
      open={checkOpen}
      onOpenChange={setCheckOpen}
      check={check}
      checking={checking}
      cellCount={allDays ? allBadGames.length : badGames.length}
      scopeLabel={allDays ? null : formatCalendarDate(date, 'EEE d MMM', date)}
      offScopeCount={allDays ? 0 : allBadGames.length - badGames.length}
      teamSummaryHref={(regId) =>
        `/admin-dashboard/events/${eventId}/scheduling/team-summary?team=${regId}`}
      setupHref={`/admin-dashboard/events/${eventId}/schedule-setup`}
      focusKind={checkFocusKind}
      markedKey={marked?.key ?? null}
      onMarkFinding={markFinding}
      onRunCheck={() => void loadCheck()}
      onFocusGame={focusCheckRow}
      onFocusTeam={(t) => {
        // No single cell to scroll to — filter the board to that team instead.
        // The division/pool narrowing is cleared so the team is always offered.
        if (!t?.registration_id) return;
        setFilterScope('grid');
        setFDivision('all');
        setFPool('all');
        setFTeam(String(t.registration_id));
        setDate(ALL_DAYS);
      }}
      onPublishAnyway={onPublishAnyway}
      onApproveBackToBack={approveB2B}
      onUnapproveBackToBack={unapproveB2B}
    />
    </>




    <DragOverlay dropAnimation={null}>
      {activeGame ? (
        <div className="w-[210px] cursor-grabbing">
          <div className="h-[56px]">
            <GameBody
              g={activeGame}
              warns={warnings[activeGame.id] ?? []}
              lifted
              color={divisionColors[activeGame.division] ?? null}
              moveMode
            />
          </div>
          {/* Read "these two will trade places" BEFORE releasing. */}
          {displaced && (
            <div className="mt-1 rounded border border-amber-400 bg-amber-100 px-1.5 py-1 text-[10px] leading-tight text-amber-900 shadow">
              <div className="flex items-center gap-1 font-semibold">
                <ArrowLeftRight className="h-3 w-3" /> Swaps with
              </div>
              <div className="truncate">{displaced.team_a_name}</div>
              <div className="truncate">vs {displaced.team_b_name}</div>
            </div>
          )}
        </div>
      ) : null}
    </DragOverlay>

    </DndContext>
  );
}
